const translation = {
  errorBoundary: {
    title: 'Something went wrong',
    description: 'This page could not be displayed. Your data has not been changed. Try again, or reload the page.',
    offlineTitle: 'We cannot reach the platform',
    offlineDescription: 'The connection to the platform failed. Check your internet connection and try again.',
    retry: 'Try again',
    reload: 'Reload the page',
  },
  notifications: {
    empty: 'No new notifications',
    clearAll: 'Clear all',
    clearAllConfirm: 'Clear all notifications? This cannot be undone.',
    clearAllFailed: 'Failed to clear notifications',
    markReadFailed: 'Failed to mark notification as read',
    delete: 'Delete notification',
    deleteFailed: 'Failed to delete notification',
    reminderTitle: 'Measurement due soon',
    reminderBody: 'It\'s almost time to record {{measurement}} for {{programme}}.',
    cleared: 'Notifications cleared',
    deleted: 'Notification deleted',
  },
  dataGrid: {
    noResultsFound: 'No results found',
    selectAll: 'Select all',
    selectRow: 'Select {{name}}',
    rowLabel: 'row {{id}}',
    ascending: 'Ascending',
    descending: 'Descending',
    sortByColumn: 'Sort by {{column}} ({{direction}})',
  },
  manageUsers: {
    title: 'Tenant members',
    subtitle: 'The people in your organization, and what they are here.',
    nameColumn: 'Name',
    emailColumn: 'Email',
    rolesColumn: 'Roles',
    rolesPlaceholder: 'Add a role',
    rolesSaved: 'Roles updated',
    rolesFailed: 'The roles could not be updated',
    rolesPartlySaved: 'Part of the change was saved. The rest was refused: {{reason}}',
    filterLabel: 'Role',
    filterAll: 'Everyone',
    filterNone: 'No role',
    noTenantNotice: 'Members are listed per organization, and this account does not belong to one.',
    noTenantTitle: 'No organization yet',
    noRoles: 'No role',
    add: {
      button: 'Add person',
      title: 'Add a person',
      address: 'Email or reference code',
      addressHelp: 'The exact address or code the person gave you.',
      addressRequired: 'Type an email address or reference code',
      lookUp: 'Look up',
      save: 'Add',
      found: 'Found: {{name}}. Add them to this organization?',
      alreadyMember: '{{name}} is already a member.',
      notFound: 'No account has this address or code yet. A person appears after their first sign-in.',
      lookupFailed: 'The address could not be looked up.',
      failed: 'The person could not be added.',
      added: '{{name}} added',
    },
    roles: {
      action: 'Roles',
      title: 'Roles of {{name}}',
    },
    disconnect: {
      action: 'Disconnect',
      title: 'Disconnect member',
      message: '{{name}} will no longer be a member of {{tenant}}. Their roles here are removed and their programmes here end.',
      done: '{{name}} disconnected',
      notYourself: 'You cannot disconnect yourself.',
      lastAdmin: 'This is the last administrator of this organization.',
    },
  },
  // Navigation
  navGroups: {
    general: 'General',
    programmes: 'Programmes',
    mySettings: 'Settings',
    roles: 'Roles',
  },
  navigation: {
    ownEnvironment: 'My own environment',
    platformEnvironment: 'Platform administration',
    switchEnvironment: 'Switch environment',
    manageUsers: 'Tenant members',
    dashboard: 'My Dashboard',
    foodTracking: 'Food Tracking',
    activity: 'Activity',
    sleep: 'Sleep',
    weightBmi: 'Weight/BMI',
    wellnessValues: 'Wellness Values',
    progress: 'Progress',
    insights: 'Insights',
    myCommunity: 'My Community',
    coaches: 'Coaches',
    coaching: 'Coaching',
    myProgrammes: 'My Programmes',
    programs: 'Programs',
    researchData: 'Research Data',
    education: 'Education',
    analytics: 'Analytics',
    billing: 'Billing',
    masterData: 'Master Data',
    shareData: 'Share Data',
    settings: 'Language & Region',
    profile: 'My Profile',
    dataPermissions: 'Data permissions',
    logout: 'Logout',
    metabolicMeasurements: 'My Measurements',
    weeklyData: 'Weekly Data',
    tenantSettings: 'Tenant Settings',
    dataManagement: 'Master Data',
    manageProgrammes: 'Programme Settings',
    manageProgrammeMeasurements: 'Programme Data',
    manageRoles: 'Roles',
    rolePermissions: 'Role permissions',
    globalRoles: 'Global roles',
    globalPermissions: 'Global permissions',
    tenantRoles: 'Tenant roles',
    tenantPermissions: 'Tenant permissions',
    dataProviders: 'Data providers',
    adminDataProviders: 'All FHIR servers',
    adminDocumentTypes: 'Document types',
    agent: 'Agents',
    ViewDocument: 'View Document',
    savedDocuments: 'My Documents',
    pinSidebar: 'Pin sidebar',
    unpinSidebar: 'Unpin sidebar',
  },
  // Authentication
  devLogin: {
    subtitle: 'Local development — authentication bypassed',
    roles: {
      globalAdmin: 'Global admin',
      tenantAdmin: 'Tenant admin',
      coach: 'Coach',
      member: 'Member',
      remoteMember: 'Remote member',
    },
  },
  brand: {
    loading: 'Loading',
  },
  auth: {
    platformName: 'My Lifestyle Platform',
    poweredBy: 'powered by Stichting Je Leefstijl Als Medicijn',
    platformLogin: 'Platform Login',
    password: 'Password',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    forgotPassword: 'Forgot password?',
    requestAccess: 'Request access',
    demoMode: 'Demo Mode - Use any credentials',
    demoDescription: 'Access your complete lifestyle wellness dashboard',
    loginSuccess: 'Login successful! Redirecting...',
    sessionExpired: {
      title: 'Your session has expired',
      message: 'To protect your data you have been signed out automatically. Sign in again to continue.',
      signIn: 'Sign in again',
      dismiss: 'Later',
    },
  },
  // Dashboard
  dashboard: {
    refreshData: 'Refresh data',
    lastSynced: 'Last synced {{time}}',
    justSynced: 'Just synced',
    signInRequired: 'You need to be signed in to view the dashboard.',
    unableToLoad: 'Unable to load dashboard data.',
    waitingForProvider_one: 'Fetching your measurements from {{providers}}.',
    waitingForProvider_other: 'Fetching your measurements from {{providers}}.',
    providerDidNotAnswer_one:
      '{{providers}} did not answer, so anything it holds is missing from this page. What is stored here is shown and is correct.',
    providerDidNotAnswer_other:
      'These sources did not answer: {{providers}}. Anything they hold is missing from this page. What is stored here is shown and is correct.',
    activeProgrammes: 'Active Programmes',
    programmes: 'Programmes',
    trackedMeasurements: 'Tracked measurements',
    targetsConfigured: 'Targets configured',
    dateAxisLabel: 'Date',
    measurementTrends: 'Measurement trends',
    measurementTrendsSubtitle: 'Stacked view to explore all measurements together.',
    timeRange: 'Time range:',
    oneYear: '1 year',
    days: '{{count}}d',
    noMeasurementData: 'No measurement data yet',
    noMeasurementDataSubtitle: 'When new data points arrive, the timeline will update automatically.',
    programmeTargets: 'Programme targets',
    programmeTargetsSubtitle: 'Targets are set by your programme administrator and displayed here for reference.',
    noProgrammeMeasurements: 'No programme measurements available yet.',
    latestValue: 'Latest value',
    targetRange: 'Target range',
    noMeasurementRecorded: 'No measurement recorded yet',
    viewMode: 'View',
    viewSimple: 'Simple',
    viewDetailed: 'Detailed',
    lastMeasured: 'Last measured {{date}}',
    onTarget: 'On target',
    needsAttention: 'Needs attention',
    noTargetSet: 'No target set',
    min: 'Min: {{value}} {{unit}}',
    max: 'Max: {{value}} {{unit}}',
    rangeTarget: '{{min}} - {{max}} {{unit}}',
    firstDashboardName: 'General',
    doneArranging: 'Done',
    greetingMorning: 'Good morning{{name}}',
    greetingAfternoon: 'Good afternoon{{name}}',
    greetingEvening: 'Good evening{{name}}',
    updatedAt: 'Updated at {{time}}',
    editLayout: 'Adjust layout',
    kindProgramme: 'Programme',
    kindPersonal: 'Personal',
    goalsInRange: '{{ok}} of {{total}} goals in range',
    noGoalsToCount: 'No goal with a reading yet',
    copyTitle: 'Make your own version',
    copyBody: 'This dashboard comes from your programme and changes with it. Give your own version a name; you can arrange that one yourself.',
    copyName: 'Name',
    copyCreate: 'Create',
    copyNameTaken: 'You already have a dashboard with that name. Choose another.',
    copyFailed: 'Your own version could not be made.',
    dashboardsLabel: 'Dashboards',
    switchDashboard: '{{name}}: switch dashboard',
    chooseDashboard: 'Choose dashboard',
    goalInRange: 'In range',
    goalOffTarget: 'Off target',
    goalNone: 'No goal',
    goalNoData: 'No data',
    sinceStart: '{{change}} since start',
    goalBetween: 'Goal {{minimum}}–{{maximum}}',
    goalAtMost: 'Goal ≤ {{value}}',
    goalAtLeast: 'Goal ≥ {{value}}',
    measuredOn: 'Measured on {{date}}',
    noReadingIn: 'No reading in {{unit}} yet',
    noReading: 'No reading yet',
    addReading: '+ Add a reading',
    arrangeHint: 'Each card now shows what it is for, how wide and how tall it is, and a trash button to take it off. Drag a card by its handle or pull the bottom-right corner to resize. Add more from the gallery above. Your arrangement is saved when you press Done.',
    saveFailed: 'Your arrangement could not be saved.',
    arrangementRepaired:
      'Your arrangement could not be stored, so the dashboard has been set back to the basic one.',
    deleteFailed: 'That dashboard could not be deleted.',
    dragWidget: 'Move this block: drag it, or use the arrow keys',
    widgetWidth: 'Width',
    widgetHeight: 'Height',
    removeWidget: 'Remove from this dashboard',
    unknownMeasurement: 'Unknown measurement',
    widgetActiveProgrammes: 'Programs',
    widgetTrackedMeasurements: 'Measurements',
    widgetTargetsConfigured: 'Targets',
    widgetSummary: 'Latest value',
    widgetTrend: 'Detailed trend',
    widgetSparkline: 'Simple trend',
    widgetMini: 'Mini trend',
    widgetRange: 'Range marker',
    widgetRecentTrends: 'Recent changes',
    aboutActiveProgrammes: 'How many programs you are taking part in.',
    aboutTrackedMeasurements: 'How many measurements are being tracked for you.',
    aboutTargetsConfigured: 'How many of those measurements your program has set a range for.',
    aboutRecentTrends: 'How far one measurement has moved over four spans, with where you were against the range.',
    aboutSummary: 'The most recent reading of one measurement, and when it was taken.',
    aboutRange: 'Where the latest reading falls against the range being aimed at.',
    aboutSparkline: 'The shape of one measurement over time, without the axes.',
    aboutMini: 'The latest reading over the shape of how it got there, in the smallest card.',
    aboutTrend: 'Every reading, with a strip underneath for choosing the period.',
    galleryTitle: 'Add widgets',
    galleryHint: 'Pick a block to put it at the bottom of your dashboard. Which measurement it shows, how wide and how tall are set on the block itself.',
    galleryAlreadyAdded: 'Already on this dashboard',
    galleryNoMeasurements: 'No measurements yet',
    changeTotal: 'All time',
    changeYear: 'Year',
    changeMonth: 'Month',
    changePrevious: 'Last',
    changeTotalAbout: 'Since the first reading recorded',
    changeYearAbout: 'Against the most recent reading that is at least a year old',
    changeMonthAbout: 'Against the most recent reading that is at least a month old',
    changePreviousAbout: 'Against the reading before the latest one',
    changeUp: 'up by',
    changeDown: 'down by',
    widthFull: 'Full width',
    widthHalf: 'Two across',
    widthThird: 'Three across',
    widthQuarter: 'Four across',
    heightShort: 'Short',
    heightMedium: 'Medium',
    heightTall: 'Tall',
    heightCustom: 'Custom ({{rows}})',
    bandFrom: 'from',
    bandUpTo: 'up to',
    bandProgramme: 'programme target',
    settingsTitle: 'Configure dashboard',
    settingsClose: 'Close',
    dashboardName: 'Name',
    opensByDefault: 'Opens by default',
    defaultExplainer: 'Press a name to show it. The one you mark is the one that opens.',
    saveSettings: 'Save',
    yourDashboards: 'Your dashboards',
    deleteDashboard: 'Delete this dashboard',
    deleteConfirmTitle: 'Delete {{name}}?',
    deleteConfirmBody:
      'The dashboard goes, and the arrangement on it goes with it. This cannot be undone.',
    deleteConfirmCancel: 'Cancel',
    deleteConfirmDelete: 'Delete',
    newDashboard: 'New dashboard',
    addDashboard: 'Add',
    measurementColumn: 'Measurement',
    scopeStrip: 'Drag to choose which stretch of time the graph shows',
    standing: {
      inside: 'Within the range being aimed at',
      above: 'Above the range being aimed at',
      below: 'Below the range being aimed at',
    },
    scopeFrom: 'Show readings from',
    scopeTo: 'Show readings up to and including',
    saved: 'Your arrangement is saved.',
    settingsSaved: '{{name}} is saved.',
    deleted: '{{name}} is deleted.',
  },
  profile: {
    welcomeNewUser: 'Welcome! Please complete your profile',
    newUserMessage: 'Help us personalise your experience by filling in your details below.',
    continueToDashboard: 'Continue to Dashboard',
    myProfile: 'My Profile',
    reviewDetails: 'Review and update your personal details',
    completeProfile: 'Complete your profile',
    keepUpdated: 'Keep your information up to date for better personalised insights',
    accountInformation: 'Account Information',
    referenceCode: 'Reference Code',
    anonymousSharingId: 'Your anonymous sharing ID',
    activeSince: 'Active since',
    personalDetails: 'Personal Details',
    firstName: 'First name',
    lastName: 'Last name',
    nickname: 'Nickname',
    nicknamePlaceholder: 'How would you like to be called?',
    birthDate: 'Birth date',
    gender: 'Gender',
    genderMale: 'Male',
    genderFemale: 'Female',
    genderPrefernottosay: 'Prefer not to say',
    emailAddress: 'Email Address',
    emailPlaceholder: 'your.email@example.com',
    emailHelper: 'Email address for updates and notifications',
    aboutMe: 'About Me',
    headline: 'Headline',
    headlinePlaceholder: 'A short tagline about yourself',
    bio: 'Bio',
    bioPlaceholder: 'Tell us a bit about yourself',
    motivation: 'Motivation',
    motivationPlaceholder: 'What motivates you to use this platform?',
    privacyDataSharing: 'Privacy & Data Sharing',
    sharingPreference: 'Sharing Preference',
    sharingHelper: 'Choose how you want to share your data on this platform',
    shareAnonymously: 'Share my data anonymously',
    shareWithName: 'Share my data with my name attached',
    doNotShare: 'Do not share my data',
    notificationPreferences: 'Notification Preferences',
    pushNotifications: 'Push notifications',
    pushNotificationsDesc: 'Receive push notifications on your device',
    emailNotifications: 'Email notifications',
    emailNotificationsDesc: 'Receive notifications via email',
    inAppNotifications: 'In-app notifications',
    inAppNotificationsDesc: 'Receive notifications within the application',
    programmeNotifications: 'Programme Notifications',
    programmeNotificationsHelper: 'Choose how many minutes before a scheduled measurement you\'d like to be reminded, per programme. Turn off Notifications for a programme to stop reminders for it entirely.',
    reminderTime: 'Reminder time',
    reminderTimeCustomLabel: 'Minutes (1-180)',
    reminderNoScheduledMeasurements: 'This programme has no scheduled measurements yet.',
    notificationsLabel: 'Notifications',
    reminderLoadFailed: 'Failed to load reminder settings',
    saving: 'Saving...',
    saveProfile: 'Save Profile',
    saveSuccess: 'Profile saved successfully',
    errorLoading: 'Failed to load profile',
    errorSaving: 'Failed to save profile',
    deleteAccount: 'Delete Account',
    deleteAccountTitle: 'Delete Account?',
    deleteAccountWarning: 'Are you sure you want to delete all your data? Have you exported all the data you want to keep? You cannot retrieve it anymore as this action is irreversible.',
    no: 'No',
    yes: 'Yes',
    confirmDeletionTitle: 'Confirm Account Deletion',
    confirmDeletionInstructionPrefix: 'If you are sure that you want to wipe all your data from the system, please type',
    confirmDeletionInstructionSuffix: 'in the text box and confirm',
    typeToConfirmPlaceholder: 'Type DELETE MY ACCOUNT here',
    cancel: 'Cancel',
    deleting: 'Deleting...',
    confirmDeletion: 'Confirm Deletion',
  },
  uploadPDF: {
    typesFailed: 'The document types could not be read.',
    title: 'Upload Document',
    description: 'This is where you can upload your files.',
    documentType: 'Document type',
    selectDocumentType: 'Select Document Type',
    labTest: 'Lab Test',
    insuranceDocument: 'Insurance document',
    idCard: 'ID card',
    enabledAIExtraction: 'Enabled: We will extract observations after upload.',
    disabledAIExtraction: 'Disabled: We will only store the document.',
    descriptionTitle: 'Description',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'e.g., Blood test from GP Visit (24/01/2026)',
    filenameTitle: 'File name',
    filenameLabel: 'File name',
    filenamePlaceholder: 'Enter a file name',
    filenameRequired: 'Please enter a file name.',
    aiExtractionTitle: 'AI Extraction',
    aiExtractionSelectTypeHelper: 'Select a document type to see AI extraction options.',
    dropFilesPrompt: 'Drop files here, or select from your device',
    chooseFiles: 'Choose File(s)',
    deleteDocument: 'Clear selection',
    filesSelected: 'file selected',
    uploadButton: 'Upload',
    documentSuccessfullyUploaded: 'Document successfully uploaded.',
    selectDocumentTypeAlert: 'Please select a document type before uploading.',
    uploadFailed: 'Failed to upload document. Please try again.',
    uploadError: 'An error occurred while uploading the document. Please try again.',
    selectDocumentFileAlert: 'Please select a file to upload.',
    selectFileAlert: 'Please select a file before uploading.',
    aiExtractionNotAvailable: 'AI extraction is not available for this document type',
    downloadError: 'Failed to download document',

  },
  documentTypes: {
    title: 'Document types',
    subtitle: 'The types members choose from when they add a document. A type that documents use is deactivated, not removed.',
    add: 'Add document type',
    columnName: 'Name',
    columnCode: 'Code',
    columnExtraction: 'AI extraction offered',
    columnDescription: 'Description',
    yes: 'Yes',
    no: 'No',
    edit: 'Edit document type',
    remove: 'Remove document type',
    deactivate: 'Deactivate',
    activate: 'Activate',
    actionFailed: 'That did not work: {{detail}}',
    messages: {
      added: '{{name}} was added.',
      updated: '{{name}} was saved.',
      removed: '{{name}} was removed.',
      removedDeactivated: '{{name}} is used by documents, so it was deactivated instead of removed.',
      activated: '{{name}} is active again.',
      deactivated: '{{name}} is deactivated.',
    },
    dialog: {
      titleAdd: 'Add a document type',
      titleEdit: 'Edit document type',
      code: 'Code',
      codeHelp: 'Capitals, digits and underscores, up to 20 characters. It cannot be changed once saved.',
      codeLocked: 'The code cannot be changed.',
      codeRequired: 'Enter a code.',
      codeFormat: 'Use capitals, digits and underscores only.',
      codeTaken: 'Another document type has this code.',
      name: 'Name',
      nameRequired: 'Enter a name.',
      nameTaken: 'Another document type has this name.',
      description: 'Description',
      extractionRequired: 'Offer AI extraction',
      extractionRequiredHelp: 'When a member adds a document of this type, AI extraction is offered and ticked. They can untick it.',
      active: 'Active',
      activeHelp: 'An inactive type is not offered when a member adds a document. Documents that have it keep it.',
      saveFailed: 'The document type could not be saved.',
    },
    removeDialog: {
      title: 'Remove this document type?',
      messageDeletes: '{{name}} is deleted. No document has this type.',
      messageDeactivates: '{{name}} is used by documents, so it is deactivated instead: members can no longer choose it, and their documents keep it. You can activate it again.',
      remove: 'Remove',
    },
  },
  documentPage: {
    document: 'Document',
    extractions: 'Extractions',
    loadFailedTitle: 'This document could not be read',
    loadFailed: 'Try again in a moment.',
    notFoundTitle: 'This document was not found',
    notFoundBody: 'It may have been deleted, or it is not yours.',
    noExtractionTitle: 'No extraction was made',
    noExtractionBody: 'This document was added without AI extraction, so nothing was read from it.',
    extractionNothingYetBody: 'The extraction has nothing to show yet.',
    extractionFailedBody: 'The extraction failed, so nothing was read from this document.',
    extractionLoadFailedTitle: 'The extractions could not be read',
    extractionLoadFailed: 'Try again in a moment.',
    referenceRange: 'Reference range',
    measuredAt: 'Measured {{date}}',
  },
  savedDocuments: {
    edit: 'Edit document',
    fileNameRequired: 'Enter a file name.',
    messages: {
      removed: '{{name}} was deleted.',
    },
    removeDialog: {
      title: 'Delete this document?',
      message: '{{name}} and its file are deleted. This cannot be undone.',
    },
    fileName: 'File Name',
    fileType: 'File Type',
    uploadedBy: 'Uploaded by',
    description: 'Description',
    uploadDate: 'Upload Date',
    status: 'Status',
    inProgress: 'In Progress',
    readyForReview: 'Ready for Review',
    committed: 'Committed',
    inReview: 'In Review',
    uploaded: 'Uploaded',
    failed: 'Failed',
    unknownStatus: 'Failed',
    noStatus: 'No status',
    noDocuments: 'No documents found',
    yourDocuments: 'Your documents',
    viewPreviouslyUploaded: 'View previously uploaded documents',
    documents: 'Documents',
    filter: 'Filter',
    allTypes: 'All',
    preview: 'Preview',
    uploadNewDocument: 'Upload a new document',
    review: 'Review',
    delete: 'Delete',
    updateDocument: 'Update Document',
    update: 'Update',
    cancel: 'Cancel',
    save: 'Save',
    documentSuccessfullyDeleted: 'Document successfully deleted',
    deleteError: 'Error deleting document(s)',
    deleteErrorPartial_one: '{{count}} document could not be deleted',
    deleteErrorPartial_other: '{{count}} documents could not be deleted',
    downloadError: 'Failed to download document',
    deleteConfirm: 'Are you sure you want to delete {{count}} document(s)?',
    deleting: 'Deleting...',
    close: 'Close',
    download: 'Download',
    types: {
      bloodTestReport: 'Blood Test Report',
      stoolTestReport: 'Stool Test Report',
      consultationNote: 'Consultation Note',
    },
    updateSuccess: 'Document updated',
    updateError: 'The document could not be updated',
  },
  documentExtractionReview: {
    aiChip: 'TRPI Powered by AI',
    title: 'Extraction Review',
    previewTitle: 'Document Preview',
    preview: {
      loadingDocument: 'Loading document...',
      preparingPdf: 'Preparing PDF...',
      zoom: 'Zoom {{percent}}%',
      zoomControlsAria: 'Zoom controls',
      zoomOut: 'Zoom out',
      fit: 'Fit',
      zoomIn: 'Zoom in',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit fullscreen',
      unavailable: 'In-app preview is unavailable for this file. Download this file to view it.',
      pageOf: 'Page {{current}} of {{total}}',
      prevPageAria: 'Previous page',
      nextPageAria: 'Next page',
    },
    subtitle: 'Review extracted values before committing to your core observations.',
    committedInfo: 'This document cannot be edited any further, you can still inspect the extracted values.',
    commit: {
      action: 'Commit to Core',
      committed: 'Committed',
      aria: 'Commit extracted measurements',
      success: 'Committed document',
      error: 'Failed to commit document',
    },
    tooltips: {
      download: 'Download',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit fullscreen',
    },
    aria: {
      download: 'Download document',
      fullscreen: 'Toggle fullscreen right panel',
    },
    updatedAt: 'Updated At: {{date}}',
    failedMessage: 'Extraction failed. You can still preview the document. Please reach out to support if the issue persists.',
    loadDataError: 'Error loading extracted data. Please try again.',
    extractionInProgress: 'Extraction is in progress.',
    notExtracted: 'Not extracted',
    timeline: {
      button: 'See Timeline',
      title: 'Extraction Timeline',
    },
    loading: 'Loading extraction...',
    providers: {
      organisation: 'organisation',
      practitioner: 'practitioner',
      searchPlaceholder: 'Search for existing {{type}}',
      notInList: 'It is not in the list',
    },
    sections: {
      measurements: {
        title: 'Extracted Measurements',
        columns: {
          measurementName: 'Extracted Measurement',
          valueExtracted: 'Extracted Value',
          unitExtracted: 'Unit (Extracted)',
          ucumCode: 'UCUM Code',
          ucumName: 'UCUM Name',
          loincCode: 'LOINC Code',
          loincName: 'LOINC Name',
          referenceRangeMin: 'Ref Min',
          referenceRangeMax: 'Ref Max',
        },
        actions: {
          resetColumns: 'Reset column visibility',
          edit: 'Edit measurements',
          save: 'Save measurements',
          cancel: 'Cancel measurements changes',
          deleteRow: 'Delete row',
          deleteRowDisabled: 'Enable edit mode to delete rows',
          addRow: 'Add row',
        },
        aria: {
          resetColumns: 'Reset column visibility',
          edit: 'Edit extracted measurements',
          save: 'Save extracted measurements',
          cancel: 'Cancel extracted measurements changes',
          deleteRow: 'Delete row {{id}}',
        },
        toasts: {
          addSuccess: 'Added measurement row',
          addError: 'Failed to add measurement row',
          deleteSuccess: 'Deleted measurement',
          deleteError: 'Failed to delete measurement',
        },
      },
      organisation: {
        title: 'Performing Organisation',
        status: {
          matchFound: 'Organisation match found',
          noMatch: 'No organisation match',
        },
        tooltips: {
          validation: 'Please fill in the right values',
          save: 'Save organisation details',
          cancel: 'Cancel organisation changes',
        },
        aria: {
          edit: 'Edit performing organisation',
          save: 'Save performing organisation changes',
          cancel: 'Cancel extracted organisation changes',
        },
        fields: {
          name: 'Name',
          address: 'Address',
          houseNumber: 'House Number',
          city: 'City',
          postalCode: 'Postal Code',
          countryCode: 'Country Code',
          contactPerson: 'Contact Person',
          phoneNumber: 'Phone Number',
          emailAddress: 'Email Address',
        },
      },
      practitioner: {
        title: 'Requesting Practitioner',
        status: {
          matchFound: 'Practitioner match found',
          noMatch: 'No practitioner match',
        },
        tooltips: {
          validation: 'Please fill in the right values',
          save: 'Save practitioner details',
          cancel: 'Cancel practitioner changes',
        },
        aria: {
          edit: 'Edit requesting practitioner',
          save: 'Save requesting practitioner changes',
          cancel: 'Cancel extracted practitioner changes',
        },
        fields: {
          name: 'Name',
          role: 'Role',
          phoneNumber: 'Phone Number',
          emailAddress: 'Email Address',
        },
      },
    },
    sectionSaved: 'Your changes are saved.',
    sectionSaveFailed: 'Your changes could not be saved.',
  },
  // Master Data Management
  masterData: {
    tabs: {
      measurements: 'Measurements',
      units: 'Units',
    },
    required: 'Required',
    remove: 'Remove',
    measurementList: {
      title: 'Measurements',
      subtitle: 'Every code the platform knows: LOINC\'s release and the platform\'s own. Open one to see its axes and units.',
      add: 'Add measurement',
      edit: 'Edit measurement',
      remove: 'Remove measurement',
      columns: {
        name: 'Name',
        code: 'Code',
        dataType: 'Kind of value',
        loincCode: 'LOINC code',
        description: 'Description',
      },
      removeDialog: {
        title: 'Remove this measurement?',
        message: '{{name}} leaves Master Data for every organization. A measurement that still has units, is in a programme or has values recorded is not removed.',
      },
    },
    measurementDialog: {
      addTitle: 'Add a measurement',
      editTitle: 'Edit {{name}}',
      name: 'Name',
      code: 'Code',
      codeHelp: 'How integrations and programmes know it, such as WEIGHT. No two measurements share one.',
      dataType: 'Kind of value',
      loincCode: 'LOINC code',
      loincHelp: 'Optional. A code of the LOINC release the platform holds, such as 29463-7.',
      description: 'Description',
    },
    dataTypes: {
      number: 'Number',
      text: 'Text',
      boolean: 'Yes or no',
      date: 'Date',
    },
    dimensionWords: {
      length: 'length',
      mass: 'mass',
      time: 'time',
      amountOfSubstance: 'amount of substance',
      electricCurrent: 'electric current',
      temperature: 'temperature',
      luminousIntensity: 'luminous intensity',
      dimensionless: 'dimensionless',
      per: 'per',
    },
    unitList: {
      title: 'Units',
      subtitle: 'The units values can be recorded in, for every organization, with their dimension. Which measurements use one is set on each measurement.',
      add: 'Add unit',
      edit: 'Edit unit',
      remove: 'Remove unit',
      columns: {
        name: 'Name',
        dimension: 'Dimension',
        ucumCode: 'UCUM code',
        quantityKinds: 'Quantities',
      },
      removeDialog: {
        title: 'Remove this unit?',
        message: '{{name}} leaves Master Data for every organization. A unit a measurement uses, or that values are recorded in, is not removed.',
      },
      noUcum: 'No UCUM code',
      noDimension: 'No dimension',
    },
    unitDialog: {
      addTitle: 'Add a unit',
      editTitle: 'Edit {{name}}',
      name: 'Name',
      dimension: 'Dimension',
      dimensionHelp: 'A unit with no UCUM code has its dimension entered here. Units of one dimension can convert into each other.',
      noDimension: 'No dimension',
      ucumCode: 'UCUM code',
      ucumHelp: 'A code of the UCUM release the platform holds, such as kg. No two units share one.',
      noUcum: 'This unit has no UCUM code. Add one if it has one, such as kg.',
      workedOut: 'Worked out from the UCUM code.',
      workedOutAfterSave: 'Worked out when you save.',
      quantityKinds: 'Quantities it may express',
      quantityKindsHelp: 'A unit fits a measurement when it may express the quantity the measurement names. Taken from QUDT, or chosen here.',
      dimensionsFailed: 'The dimensions could not be read, so none can be chosen.',
      dimensionsRetry: 'Try again',
    },
    measurementPage: {
      units: 'Units',
      notFoundTitle: 'This measurement could not be found',
      notFoundBody: 'It may have been removed, or the address is wrong.',
      loadFailedTitle: 'This measurement could not be read',
      loadFailed: 'Try again in a moment.',
    },
    measurementUnits: {
      add: 'Link a unit',
      edit: 'Edit link',
      remove: 'Remove link',
      isDefault: 'Canonical',
      columns: {
        unit: 'Unit',
        multiplier: 'Multiplier',
        offset: 'Offset',
        isDefault: 'Canonical',
        precision: 'Decimals',
        conversion: 'Conversion to the canonical unit',
        approvedBy: 'Approved by',
        validMinimum: 'Valid from',
        validMaximum: 'Valid up to',
      },
      removeDialog: {
        title: 'Remove this link?',
        message: '{{unit}} is no longer offered for {{measurement}}, in any organization. Values already recorded keep what was stored.',
      },
      confirm: 'Confirm factor',
      confirmedToast: 'Factor for {{unit}} confirmed',
      factor: '1 {{from}} = {{multiplier}} {{to}}',
      factorWithOffset: '({{from}} + {{offset}}) × {{multiplier}} = {{to}}',
      viaAlgebra: 'Worked out from the UCUM codes.',
      viaMolarMass: 'Through the molar mass of {{analyte}}, {{mass}} g/mol. Source: {{source}}.',
      formula: '{{factor}}. {{via}}',
      approved: '{{by}}, {{date}}',
      notApproved: 'Not approved yet',
      unavailable: {
        dimensionDiffers: 'No factor: this unit is of another dimension than the canonical unit.',
        noCanonicalUnit: 'No factor yet: the measurement has no canonical unit.',
        noMolarMass: 'No factor yet: the molar mass of the analyte is not on record.',
        noDimension: 'No factor: this unit has no dimension.',
      },
      enterMolarMass: 'Enter the molar mass',
    },
    unitLinkDialog: {
      addTitle: 'Add a unit to {{measurement}}',
      editTitle: 'Edit {{unit}} for {{measurement}}',
      unit: 'Unit',
      noUnitsLeft: 'Every unit is already a unit of this measurement. Add one under Units first.',
      noUnits: 'There are no units yet. Add one under Units first.',
      multiplier: 'Multiplier',
      multiplierHelp: 'A value in this unit, times this, is the value in the canonical unit.',
      multiplierWrong: 'Enter a number other than 0, with up to ten digits before and after the point.',
      offset: 'Offset',
      offsetHelp: 'Added before multiplying: 0 for most units, -32 for degrees Fahrenheit.',
      numberWrong: 'Enter a number with up to ten digits before and after the point.',
      conversionChanged: 'Every organization\'s values in {{unit}} are stored and shown through this conversion, so changing it changes them everywhere.',
      precision: 'Decimals',
      precisionHelp: 'Optional. How many decimals a value in this unit is shown with.',
      precisionWrong: 'Enter a whole number from 0 to {{max}}.',
      isDefault: 'Canonical unit: values are shown in it and its range is checked',
      otherDefault: 'Another unit is the canonical one. Switch it off there first.',
      isDefaultHelp: 'The first unit of a measurement is its canonical unit. There is one.',
      validMinimum: 'Valid minimum',
      validMaximum: 'Valid maximum',
      validHelp: 'Optional. The lowest value that can be recorded, in {{unit}}. A value in another unit is converted first.',
      rangeWrong: 'The minimum is higher than the maximum.',
    },
    messages: {
      added: '{{name}} added',
      saved: '{{name}} saved',
      removed: '{{name}} removed',
    },
    measurementCodes: {
      list: {
        columns: {
          name: 'Name',
          code: 'Code',
          codeSystem: 'Code system',
          status: 'Status',
          inUse: 'In use',
        },
        yes: 'Yes',
        no: 'No',
        views: {
          label: 'Show',
          all: 'All codes',
          inUse: 'In use',
        },
        systemFilter: 'Code system',
        systemAll: 'All code systems',
      },
      general: {
        code: 'Code',
        codeSystem: 'Code system',
        name: 'Name',
        status: 'Status',
        release: 'Release',
        releaseValue: '{{version}}, loaded on {{date}}',
        releaseNone: 'Not applicable: no release carries a platform code.',
        releaseUnknown: 'Not recorded for this code.',
        valueKind: 'Kind of value',
        quantityKind: 'Kind of quantity',
      },
      axes: {
        title: 'What it measures',
        component: 'Component',
        analyte: 'Analyte',
        property: 'Property',
        time: 'Time',
        system: 'System',
        scale: 'Scale',
        method: 'Method',
        noMethod: 'None',
        unknown: 'The platform does not hold this code\'s axes. It is grouped with nothing.',
      },
      groups: {
        title: 'Grouped with',
        nothing: 'Grouped with nothing at this level.',
      },
      levels: {
        THIS_CODE: 'This code only',
        ANY_METHOD: 'The same thing, whatever the method',
      },
      trace: {
        title: 'Trace a value',
        help: 'Enter the number a member gives you for a value. You see what it was recorded as and where it came from.',
        number: 'Value number',
        show: 'Show origin',
      },
      dialog: {
        addTitle: 'Add a platform measurement',
        editTitle: 'Edit {{name}}',
        name: 'Name',
        code: 'Code',
        codeHelp: 'Starts with X, such as X-HAPPY-1. No two measurements share one.',
        codeSystem: 'Code system',
        analyteHelp: 'Leave empty to use the component.',
        noPart: 'No part matches. Type a name to add one.',
        partsFailed: 'The parts could not be read. Try typing again.',
        addPart: 'Add "{{name}}" as a new part',
        partAdded: '{{name}} added as a part',
        newPropertyKind: 'Kind of quantity for a new property',
        newPropertyKindHelp: 'Used only when you add a new property above.',
        noKind: 'None',
      },
    },
    unitTabs: {
      label: 'Units and molar masses',
      units: 'Unit list',
      molarMasses: 'Molar masses',
    },
    molarMasses: {
      title: 'Molar masses',
      subtitle: 'A mass concentration meets a molar concentration of the same analyte through its molar mass. Each mass has its source.',
      add: 'Add molar mass',
      edit: 'Edit molar mass',
      remove: 'Remove molar mass',
      addTitle: 'Add a molar mass',
      editTitle: 'Edit the molar mass of {{name}}',
      columns: {
        analyte: 'Analyte',
        gramsPerMole: 'Grams per mole',
        source: 'Source',
      },
      analyte: 'Analyte',
      analyteHelp: 'Search by name or Part number, such as Glucose.',
      gramsPerMole: 'Grams per mole',
      massHelp: 'For glucose: 180.156.',
      massWrong: 'Enter a number above 0, with up to ten digits before and after the point.',
      source: 'Source',
      sourceHelp: 'Where the number came from: a LOINC group, a ChEBI or PubChem identifier, a reference work.',
      removeDialog: {
        title: 'Remove this molar mass?',
        message: '{{name}} has no molar mass any more. Factors that use it can no longer be worked out.',
      },
    },
  },
  // Observations
  tenantSettings: {
    title: 'Tenant Settings',
    subtitle: 'Manage settings',
    loadError: 'Failed to load tenant settings. You may not have permission to view this tenant.',
    generalInfo: 'General Information',
    tenantName: 'Tenant Name',
    organisation: 'Organization',
    organisationHelper: 'Organization cannot be changed',
    tenantId: 'Tenant ID',
    yourRole: 'Your Role',
    roleDescription: 'Your role in this organization is {{role}}.',
    permissionsConfigured: 'What this role may do is configured per organization. An administrator can see and change it on the roles screen.',
    saveName: 'Save name',
    nameSaved: 'The tenant name has been updated.',
    saveError: 'The name could not be saved. You may not have permission to change it.',
  },
  communityUsers: {
    title: 'Community Users',
    subtitle: 'All users who have an account in the platform. Personal details are only shown based on each user\'s privacy preferences.',
    loadError: 'Failed to load community users. Please try again.',
    view: 'View',
    noUsersTitle: 'No shared users',
    noUsersNotice: 'Nobody has made their data available to view yet.',
  },
  observations: {
    title: 'Add My Observations',
    subtitle: 'Enter and view your health measurements and observations',
    viewToggleLabel: 'View',
    viewCalendar: 'Calendar',
    viewStack: 'Stack',
    viewTimeline: 'Timeline',
    refresh: 'Refresh',
    noMeasurements: 'No measurements available. Please enroll in a programme first.',
    addObservation: 'Add Observation',
    submitting: 'Submitting...',
    searchBy: 'Search by',
    searchValue: 'Search value',
    columns: 'Columns',
    columnsLabel: 'Change Displayed Columns',
    eventType: 'Event type',
    dateTime: 'Date time',
    measurementType: 'Measurement type',
    value: 'Value',
    sourceDocument: 'Source document',
    sourceDocumentDescription: 'Document description',
    actions: 'Actions',
    loading: 'Loading your measurements…',
    filter: {
      title: 'Filter',
      label: 'Programmes',
      all: 'All programmes',
      notInProgramme: 'Not in a programme',
    },
    units: {
      systemOption: 'System ({{unit}})',
      defaultOption: '{{unit}} (default)',
    },
    source: {
      column: 'Storage',
      local: 'This platform',
      remoteLive: 'Provider',
      remoteCached: 'Copy',
      storedAtProvider: 'To {{provider}}',
      storedAtProviderHelp:
        'What you enter here is stored at {{provider}}. This platform keeps no copy of its own.',
      remoteEventType: 'Record from provider',
      tooltipLocal: 'This platform holds this measurement.',
      tooltipProvider: 'Fetched from {{provider}}',
      tooltipFetchedAt: 'read at {{fetchedAt}}',
      deleteUnavailable:
        'This measurement is held by a provider and cannot be deleted here. ' +
        'End the connection in your profile settings.',
      viewSources: 'View sources',
      dialogTitle: 'Where these measurements come from',
      unnamed: 'Unnamed source',
      entriesStoredHere: 'A measurement you add here is stored by this platform.',
      managePermissions: 'Open data permissions',
      measurementsRead: 'Measurements read: {{measurements}}',
      codesRequested: 'Asked for {{codes}}, and nothing came back this time.',
      nothingRequested:
        'No measurements are enabled for this source, so it was not asked for ' +
        'anything.',
      notContactedDetail:
        'This source was not asked for anything on this view: its measurements ' +
        'are already stored here, so they are in the list above.',
      summary: {
        connected:
          'These measurements are read from {{count}} connected source(s), ' +
          'alongside the ones stored here.',
        someUnavailable:
          'A connected source could not be reached, so part of your record is ' +
          'missing from this list right now. Nothing has been lost.',
        someStale:
          'Some of these measurements come from a stored copy that may be out ' +
          'of date.',
        nothingEnabled:
          'No measurements are enabled for your connected source(s), so ' +
          'nothing was read from them.',
      },
      state: {
        reading: 'Read',
        stale: 'Copy may be old',
        nothingEnabled: 'Nothing enabled',
        unavailable: 'Could not be reached',
        notContacted: 'Not read this time',
      },
    },
    eventTypes: {
      manualInput: 'Manual Input',
      deviceSync: 'Device Sync',
      uploaded: 'Uploaded',
      coachingSession: 'Coaching Session',
    },
    validation: {
      eventTypeRequired: 'Event type is required',
      dateTimeRequired: 'Date time is required',
      invalidDateTime: 'Invalid date time',
      atLeastOneMeasurement: 'Enter at least one measurement value',
      invalidValue: 'Invalid value',
      validationFailed: 'Validation failed',
      fixErrors: 'Please fix all validation errors before submitting',
    },
    messages: {
      noChanges: 'No changes to submit',
      storedAtProviders: 'Stored at {{providers}}.',
      successSingle: 'Successfully submitted observation entry!',
      successMultiple: 'Successfully submitted all observation entries!',
      submitError: 'Failed to submit measurements',
      updateError: 'Failed to update measurements',
      deleteSuccess: 'Observation deleted successfully',
      removeSuccess: 'Observation removed',
      deleteError: 'Failed to delete observation',
      loadError: 'Failed to load measurements',
      networkError: 'Network error while loading measurements',
      saveError: 'Error while saving changes',
    },
    dialog: {
      deleteTitle: 'Delete Observation',
      deleteMessage: 'Are you sure you want to delete this observation event? This action cannot be undone.',
      cancel: 'Cancel',
      delete: 'Delete',
    },
    dayView: {
      previousDay: 'Previous day',
      nextDay: 'Next day',
      today: 'Today',
      noEntries: 'No observations recorded on this day',
      edit: 'Edit',
      save: 'Save',
      dataTypeNumber: 'Number',
      dataTypeText: 'Text',
      dataTypeBoolean: 'Yes/no',
      noProgramme: 'No programme',
      noValues: 'No values recorded',
      newMeasurementTitle: 'New Measurement',
      previewPending: 'Not saved yet',
      programme: 'Programme',
      selectProgramme: 'Select a programme',
      measurement: 'Measurement',
      selectMeasurement: 'Select a measurement',
      noMeasurementSelected: 'Select a measurement before entering a value',
      noProgrammeSelected: 'Select a programme before selecting a measurement',
      value: 'Value',
      viewLabel: 'View',
      viewDay: 'Day',
      viewWeek: 'Week',
      viewMonth: 'Month',
      previousWeek: 'Previous week',
      nextWeek: 'Next week',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      noEntriesRange: 'No observations recorded in this period',
      legendTitle: 'Programmes',
      legendScheduledLabel: 'Scheduled measurement',
      legendNotInProgramme: 'Not in a programme',
      dueLabel: 'To take',
      doneLabel: 'Done',
      dueTooltip: '{{measurement}} — {{programme}}',
      recordDue: 'Record {{measurement}}',
      slotHint: 'Click any empty time on the calendar to record something new',
      moreEvents: '+{{count}} more',
    },
  },
  // Settings
  settings: {
    title: 'Language & Region Settings',
    language: 'Language',
    region: 'Region',
    systemOfMeasurement: 'System of Measurement',
    timezone: 'Timezone',
    metricSystem: 'Metric System',
    imperialSystem: 'Imperial System',
    usCustomaryUnits: 'United States Customary Units',
    regionInfoTitle: 'Region Information',
    regionInfoText: 'Choose your region to display numbers, dates, and times in local formats (e.g., US: 1,234.56, 03/07/2025, 5:30 PM; Germany: 1.234,56, 07.03.2025, 17:30).',
    regionInfo: 'Choose your region to display numbers, dates, and times in local formats.',
    selectLanguage: 'Select Language',
    selectRegion: 'Select Region',
    selectMeasurement: 'Select Measurement System',
    selectTimezone: 'Select Timezone',
    preview: 'Preview',
    formatExamples: 'Format Examples',
    save: 'Save',
    cancel: 'Cancel',
    saving: 'Saving...',
    savedSuccessfully: 'Settings saved successfully!',
    saveSuccess: 'Settings saved successfully!',
    warningTitle: 'Confirm Changes',
    warningMessage: 'These changes will take effect immediately. Language changes will update the interface right away, while regional and measurement settings will apply to all displayed data.',
    confirmSave: 'Save Changes',
    languageLabel: 'Language',
    regionLabel: 'Region',
    measurementLabel: 'Measurement',
    numberLabel: 'Number',
    dateLabel: 'Date',
    timeLabel: 'Time',
    weightLabel: 'Weight',
    heightLabel: 'Height',
    distanceLabel: 'Distance',
    temperatureLabel: 'Temperature',
    saveFailed: 'Your settings could not be saved.',
    languageSaveFailed: 'Your language choice could not be saved.',
  },
  adminDashboard: {
    menuItems: {
      questionnaires: 'Questionnaires',
      tenants: 'Tenants',
      organisations: 'Organizations',
      users: 'System users',
    },
    users: {
      title: 'System users',
      search: 'Search by email, name or organisation',
      empty: 'No users found',
      columns: {
        id: 'ID',
        email: 'Email',
        name: 'Name',
        status: 'Status',
        organisations: 'Organisations',
      },
    },
  },
  organisationDialog: {
    titleCreate: 'Create New Organization',
    titleEdit: 'Edit Organization',
    emailInvalid: 'Enter an email address like name@example.org',
    dateInvalid: 'Enter a date',
    legalNameRequired: 'Legal Name is required',
    createError: 'Failed to create organization. Please check if unique fields are already in use.',
    fields: {
      legalName: 'Legal Name',
      tradeName: 'Trade Name',
      taxId: 'Tax ID',
      referenceCode: 'Reference Code',
      emailAddress: 'Email Address',
      phoneNumber: 'Phone Number',
      website: 'Website',
      practiceCode: 'Practice Code',
      chamberOfCommerceNumber: 'Chamber of Commerce Number',
      subscriptionDate: 'Subscription Date',
      active: 'Active',
    },
    actions: {
      create: 'Create',
    },
  },
  organisationList: {
    title: 'Organizations',
    addButton: 'Add Organization',
    updateSuccess: 'Organization updated successfully',
    updateError: 'Failed to update organization',
    columns: {
      legalName: 'Legal Name',
      tradeName: 'Trade Name',
      taxId: 'Tax ID',
      referenceCode: 'Reference Code',
      emailAddress: 'Email',
      phoneNumber: 'Phone',
      website: 'Website',
      practiceCode: 'Practice Code',
      chamberOfCommerceNumber: 'Chamber of Commerce',
      subscriptionDate: 'Subscription Date',
      status: 'Status',
    },
    statusLabels: {
      active: 'Active',
      inactive: 'Inactive',
    },
    edit: 'Edit',
    deactivate: 'Deactivate',
    alreadyInactive: 'Already inactive. Set it active again with Edit.',
    deactivateTitle: 'Deactivate organization',
    deactivateMessage: '{{name}} will be marked inactive. Nothing about it is removed, and you can set it active again with Edit.',
    deactivated: 'Organization deactivated',
    created: 'Organization created',
  },
  questionnaireEditor: {
    questionsCount: 'Questions ({{count}})',
    addQuestionButton: 'Add Question',
    emptyState: {
      message: 'No questions yet. Add your first question!',
      button: 'Add Question',
    },
    question: {
      prefix: 'Question',
      required: 'Required',
      identity: 'Identity: ',
      optionsLabel: 'Options:',
    },
    tooltips: {
      edit: 'Edit',
      delete: 'Delete',
    },
    dialog: {
      titleNew: 'New Question',
      titleEdit: 'Edit Question',
      questionType: 'Question Type',
      questionPrompt: 'Question Prompt',
      requiredQuestion: 'Required question',
      identityField: 'This is an identity field (for user onboarding)',
      identityFieldType: 'Identity Field Type',
      listOptionsTitle: 'List Options',
      addOptionButton: 'Add Option',
      codeLabel: 'Code',
      codePlaceholder: 'e.g., SAT_HIGH',
      valueLabel: 'Display Value',
      valuePlaceholder: 'e.g., Very Satisfied',
      emptyOptions: 'Add at least one option for dropdown questions',
      cancel: 'Cancel',
      save: 'Save',
      create: 'Create',
      removeOption: 'Remove option',
    },
    deleteDialog: {
      title: 'Delete Question',
      message: 'Are you sure you want to delete this question? This will also delete all answers associated with it.',
      cancel: 'Cancel',
      delete: 'Delete',
    },
    messages: {
      updateSuccess: 'Question updated successfully',
      createSuccess: 'Question created successfully',
      deleteSuccess: 'Question deleted successfully',
      saveError: 'Failed to save question',
      deleteError: 'Failed to delete question',
    },
    questionTypes: {
      text: 'Text',
      number: 'Number',
      date: 'Date',
      list: 'List/Dropdown',
      boolean: 'Yes/No',
    },
    identityTypes: {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      date_of_birth: 'Date Of Birth',
    },
  },
  questionnaireList: {
    title: 'Questionnaires',
    newButton: 'Add Questionnaire',
    table: {
      name: 'Name',
      description: 'Description',
      status: 'Status',
      version: 'Version',
      publicLink: 'Public Link',
    },
    statusLabels: {
      active: 'Active',
      inactive: 'Inactive',
    },
    dialog: {
      titleNew: 'Add Questionnaire',
      titleEdit: 'Edit Questionnaire',
      name: 'Name',
      description: 'Description',
      active: 'Active',
      create: 'Create',
      nameRequired: 'Enter a name',
      publicLink: 'Public link',
      noPublicLink: 'This questionnaire has no public link yet.',
      newLink: 'Make a new link when saving. The current link stops working.',
      makeLink: 'Make a public link when saving',
    },
    deleteDialog: {
      title: 'Remove questionnaire',
      message: '{{name}} and its questions will be removed. A questionnaire people have answered is not removed; deactivate it instead.',
      delete: 'Remove',
    },
    messages: {
      updateSuccess: 'Questionnaire updated successfully',
      createSuccess: 'Questionnaire created successfully',
      deleteSuccess: 'Questionnaire removed',
      saveError: 'Failed to save questionnaire',
      linkCopied: 'Link copied to clipboard',
      copyFailed: 'The link could not be copied',
    },
    copyLink: 'Copy link',
    copyLinkOf: 'Copy the public link of {{name}}',
    noLink: 'None',
    edit: 'Edit',
    delete: 'Remove',
    noOrganisationTitle: 'No organization yet',
    noOrganisationNotice: 'Questionnaires belong to an organization, and this account belongs to none.',
  },
  submissionViewer: {
    table: {
      id: 'ID',
      status: 'Status',
      email: 'Email',
      name: 'Name',
      submittedAt: 'Submitted At',
    },
    statusLabels: {
      submitted: 'Submitted',
      draft: 'Draft',
      scored: 'Scored',
      abandoned: 'Abandoned',
    },
    dialog: {
      title: 'Submission - Answers',
      submissionDetails: 'Submission Details',
      status: 'Status',
      submittedAt: 'Submitted At',
      notYetSubmitted: 'Not yet submitted',
      email: 'Email',
      name: 'Name',
      answersCount: 'Answers ({{count}})',
      noAnswers: 'No answers submitted yet.',
      questionPrefix: 'Question {{number}}',
      close: 'Close',
      unknownQuestion: 'A question no longer in this questionnaire',
    },
    booleanValues: {
      yes: 'Yes',
      no: 'No',
    },
    answersFailed: 'The answers could not be loaded',
    openAnswers: 'Answers sent {{sent}}',
  },
  tenantList: {
    title: 'Tenants',
    addButton: 'Add Tenant',
    saveError: 'Failed to save tenant',
    noOrganisationsWarning: 'No organizations available. Create an organization first before adding tenants.',
    table: {
      id: 'ID',
      name: 'Name',
      organisation: 'Organization',
    },
    tooltips: {
      edit: 'Edit',
    },
    dialog: {
      titleCreate: 'Create Tenant',
      titleEdit: 'Edit Tenant',
      tenantName: 'Tenant Name',
      organisation: 'Organization',
      create: 'Create',
      nameRequired: 'Enter a name',
      organisationRequired: 'Choose an organization',
      organisationLocked: 'A tenant stays with its organization',
    },
    messages: {
      created: 'Tenant added',
      updated: 'Tenant saved',
    },
  },
  publicQuestionnairePage: {
    loading: 'Loading...',
    invalidLink: 'Invalid questionnaire link',
    notFound: 'Questionnaire not found or no longer active',
    checkLink: 'Please check the link and try again.',
    thankYou: 'Thank You!',
    submitted: 'Your responses have been submitted successfully.',
    newAccountCreated: 'A new account has been created for you. Check your email for login instructions.',
    progress: 'Progress',
    questions: 'questions',
    required: 'This field is required',
    identityFieldInfo: 'This information will be used to create your account',
    submitButton: 'Submit Questionnaire',
    submitting: 'Submitting...',
    autoSave: 'Your answers are saved automatically as you type',
    missingRequired: 'Please answer all required questions ({{count}} remaining)',
    submitError: 'Failed to submit questionnaire. Please try again.',
    placeholders: {
      text: 'Enter your answer...',
      number: 'Enter a number...',
      email: 'Enter your email',
      firstName: 'Enter your first name',
      lastName: 'Enter your last name',
      dateOfBirth: 'Enter your date of birth',
    },
    booleanValues: {
      yes: 'Yes',
      no: 'No',
    },
    answerSaveFailed: 'Your answer could not be saved. Please try again.',
  },
  manageProgrammeMeasurements: {
    title: 'Programme Measurements',
    tenant: 'Tenant',
    addButton: 'Add Programme Measurement',
    noProgrammes: 'No programmes available. Please create a programme first.',
    noOrganisationTitle:
      'No organization yet',
    noOrganisation: 'Programme measurements belong to an organization, and this account belongs to none.',
    rolesUnknownTitle: 'Your roles could not be read',
    rolesUnknownNotice:
      'Without them this screen cannot tell which organization to show, which is not the same as belonging to none.',
    table: {
      programmeName: 'Programme Name',
      measurementName: 'Measurement Name',
      loincCode: 'LOINC code',
      minimumTarget: 'Minimum Target',
      maximumTarget: 'Maximum Target',
      defaultUnit: 'Default Unit',
      reminder: 'Reminder',
      everyWeeks: 'Every {{count}} weeks: {{days}}',
    },
    actions: {
      edit: 'Edit',
      delete: 'Delete',
    },
    dialog: {
      titleAdd: 'Add Programme Measurement',
      titleEdit: 'Edit Programme Measurement',
      programme: 'Programme',
      measurement: 'Measurement',
      minimumTarget: 'Minimum Target',
      maximumTarget: 'Maximum Target',
      defaultUnit: 'Default Unit',
      selectProgramme: 'Select a programme',
      selectMeasurement: 'Select a measurement',
      measurementSearchHint: 'Type at least three letters of the name or the code.',
      measurementNoMatch: 'No measurement matches.',
      measurementSearchFailed: 'The measurements could not be read. Try typing again.',
      selectUnit: 'Select a unit',
      repeatEvery: 'Repeat every',
      weeks: 'weeks',
      repeatOn: 'Repeat on',
      ends: 'Ends',
      endsProgrammeEnd: 'When the programme ends',
      endsOn: 'On',
      endsAfter: 'After',
      occurrences: 'occurrences',
      timeOfDay: 'At',
      timeOfDayOptional: 'optional',
      selectWeekdaysFirstWarning: 'Select a weekday first',
      recurrenceHint: 'Select no days to record no schedule. Weeks are counted from the week the programme starts.',
      programmeHasNoEndDate: 'This programme has no end date, so it repeats indefinitely',
      save: 'Save',
      update: 'Update',
    },
    deleteConfirm: 'Are you sure you want to delete the measurement "{{measurementName}}" from the programme "{{programmeName}}"?',
    deleteTitle: 'Remove this measurement from the programme?',
    messages: {
      loadRolesFailed: 'Failed to load tenant roles',
      loadDataFailed: 'Failed to load data',
      partlyRead: 'The measurements of some programmes could not be read, so this list may be incomplete.',
      noTenantSelected: 'No tenant selected',
      selectProgrammeError: 'Please select a programme',
      selectMeasurementError: 'Please select a measurement',
      selectUnitError: 'Please select a default unit',
      recurrenceIntervalError: 'Repeat every must be a whole number of weeks between 1 and 52',
      recurrenceEndDateError: 'Choose an end date that is not before the programme starts',
      recurrenceOccurrencesError: 'Number of occurrences must be a whole number between 1 and 520',
      deleteSuccess: 'Programme measurement deleted successfully',
      addSuccess: 'Programme measurement added successfully',
      addFailed: 'Failed to add programme measurement',
      updateSuccess: 'Programme measurement updated successfully',
      updateFailed: 'Failed to update programme measurement',
      notAllowed: 'You may not change this.',
    },
  },
  manageProgrammeDialog: {
    titleCreate: 'Create New Programme',
    titleEdit: 'Edit Programme',
    programmeName: 'Programme Name',
    programmeNameRequired: 'Programme name is required',
    description: 'Description',
    targetAudience: 'Target Audience',
    label: 'Label',
    labelHelp: 'A short label members see beside the programme, such as Pilot.',
    startDate: 'Start Date',
    startDateRequired: 'Start date is required',
    startDateLocked: 'Start date cannot be changed after creation',
    endDate: 'End Date',
    endDateHelper: 'Leave empty for ongoing programmes',
    endDateError: 'End date must be after start date',
    startDatePast: 'Start date cannot be in the past',
    endDatePast: 'End date cannot be in the past',
    saveChanges: 'Save Changes',
    createProgramme: 'Create Programme',
    saveFailed: 'Failed to save programme',
    banner: {
      label: 'Banner',
      choose: 'Choose an image',
      remove: 'Remove banner',
      none: 'No banner yet. Members see the programme\'s name on the tenant\'s colour.',
      accepted: 'PNG, WebP or JPEG, at most 2 MB, between 800 × 200 and 6000 × 6000 pixels.',
      refused: 'This file cannot be used. Choose a PNG, WebP or JPEG image of at most 2 MB, between 800 × 200 and 6000 × 6000 pixels.',
      saveFailed: 'The banner could not be saved. Try again.',
    },
  },
  manageProgrammes: {
    title: 'Programmes',
    addButton: 'Add Programme',
    tenant: 'Tenant',
    loadRolesFailed: 'Failed to load tenant roles',
    loadProgrammesFailed: 'Failed to load programmes',
    table: {
      programmeName: 'Programme name',
      description: 'Description',
      target: 'Target',
      label: 'Label',
      startDate: 'Start date',
      endDate: 'End date',
    },
    actions: {
      edit: 'Edit',
      delete: 'Delete',
    },
    deleteDialog: {
      title: 'Delete Programme',
      message: 'Are you sure you want to delete the programme "{{programmeName}}"? This action cannot be undone.',
      delete: 'Delete',
    },
    messages: {
      createSuccess: 'Programme created successfully',
      updateSuccess: 'Programme updated successfully',
      deleteSuccess: 'Programme deleted successfully',
    },
    ongoing: 'Ongoing',
    loadFailedTitle: 'Programmes could not be read',
    noOrganisationNotice: 'Programmes belong to an organization, and this account belongs to none.',
    noOrganisationTitle: 'No organization yet',
    rolesUnknownNotice: 'Without them this screen cannot tell what you administer, which is not the same as administering nothing.',
    rolesUnknownTitle: 'Your roles could not be read',
  },
  programmesOverview: {
    title: 'Available Programmes',
    subtitle: 'Find a programme that fits you and join in one click.',
    loadFailed: 'Failed to load programmes',
    enrollFailed: 'Failed to enroll in programme',
    enroll: 'Enroll',
    leave: 'Leave programme',
    unenrollFailed: 'Failed to unenroll from programme',
    close: 'Close',
    retry: 'Try again',
    filters: {
      label: 'Show',
      all: 'All',
      open: 'Open to join',
      enrolled: 'Enrolled',
    },
    search: {
      label: 'Search programmes',
      clear: 'Clear search',
    },
    noMatch: {
      title: 'No programme matches',
      body: 'Clear the search or choose All.',
      clear: 'Clear search and filter',
    },
    status: {
      enrolled: 'Enrolled',
      notEnrolled: 'Not enrolled',
    },
    phase: {
      starts: 'Starts {{date}}',
      week: 'Week {{week}} of {{total}}',
      since: 'Ongoing since {{date}}',
      ended: 'Ended {{date}}',
    },
    card: {
      enrolled: '{{formatted}} enrolled',
    },
    dialog: {
      aim: 'Aim',
      runs: 'Runs',
      rhythm: 'How often you measure',
      enrolledYou: 'You\'re enrolled',
      ended: 'This programme has ended.',
      rhythmFailed: 'How often you measure could not be read.',
      enrolledPeople_one: '{{formatted}} person enrolled',
      enrolledPeople_other: '{{formatted}} people enrolled',
      runsFrom: 'From {{start}}, no end date',
      runsFromTo_one: '{{start}} to {{end}} · {{count}} week',
      runsFromTo_other: '{{start}} to {{end}} · {{count}} weeks',
    },
    rhythm: {
      daily: 'Measure every day',
      oncePerWeek: 'Measure once a week',
      timesPerWeek: 'Measure {{count}} times a week',
      everyWeeks: 'Measure every {{count}} weeks',
      weekdaysOn: 'Measuring on {{days}}',
    },
    noProgrammesNotice: 'Your organization has not opened any programmes for enrollment yet.',
    noProgrammesTitle: 'No programmes yet',
    programmesUnknownNotice: 'The list did not arrive, so this is not the same as having none.',
    programmesUnknownTitle: 'Programmes could not be read',
    confirmUnenroll: {
      title: 'Leave this programme?',
      body: 'You will be unenrolled from {{programme}}. You can join again later.',
      cancel: 'Cancel',
      confirm: 'Unenroll',
    },
    noTenantTitle: 'Not in an organization yet',
    noTenantNotice: 'An administrator of your organization adds you. Then its programmes appear here.',
  },
  // Common actions
  actions: {
    add: 'Add',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    open: 'Open',
  },
  chat: {
    page_title: 'Chat with AI',
    page_desc: 'Get lifestyle advises from our AI.',
    hero_title: 'Welcome back{{name}}',
    prompt_desc: 'How may I help you today?',
    menu_title: 'Chat',
    action_button_diets: 'Diets',
    action_button_energy: 'Energy',
    action_button_nutritions: 'Nutritions',
  },
  dataProviders: {
    addServer: 'Add a FHIR server',
    authConnected: 'Connected to {{name}}.',
    authFailed: 'The authorisation did not complete: {{detail}}',
    columnAddress: 'Address',
    columnPlatformStatus: 'Platform status',
    columnServerStatus: 'Server status',
    columnName: 'Name',
    columnOwner: 'Server owner',
    columnVisibility: 'Who can see it',
    connectionEnded: 'You ended this connection. Connecting again starts a new one.',
    empty: 'No FHIR servers registered yet',
    loadFailedTitle: 'Your data sources could not be read',
    loadFailedBody: 'This is not the same as there being nothing registered. Try again, and if it keeps happening the platform is having trouble reaching its own list.',
    loadFailedRetry: 'Try again',
    ownServerFailed: 'That server could not be added.',
    ownerAMember: 'A member',
    ownerColumnJustMe: 'Just me',
    ownerGroup: 'Group {{name}}',
    ownerHelp: 'This decides who can change the server. Who sees it is set on its page afterwards.',
    ownerJustMe: 'I am the owner',
    ownerOrganisation: 'Organisation {{name}}',
    ownerQuestion: 'Who owns this server?',
    ownerSearchPlaceholder: 'Search for yourself or an organization',
    ownerUnknown: 'Another party',
    providerRemoved: '{{name}} was removed',
    registerSubtitle: 'Every server registered on the platform, whoever owns it.',
    registerTitle: 'All FHIR servers',
    removeProvider: 'Remove server',
    subtitle: 'Servers where your measurements are. Open a source to connect it and choose what this platform may read and write back.',
    title: 'FHIR servers',
    visibleEveryone: 'Everyone on the platform',
    visibleOwnerOnly: 'Nobody but its owner',
    visibleMembersOf: 'Members of {{tenants}}',
    visibleNoTenant: 'Offered to no tenant',
    tenantOfOrganisation: '{{tenant}} ({{organisation}})',
    connection: {
      'not-connected': 'Not connected',
      'connected': 'Connected',
      'connected-untried': 'Connected, not used yet',
      'not-answering': 'Not answering',
      'ended': 'Ended',
    },
  },
  dataProviderList: {
    active: 'Active',
    inactive: 'Inactive',
    paused: 'Paused',
    pausedSince: 'Paused since {{when}}',
    deactivate: 'Deactivate',
    activate: 'Activate',
    pause: 'Pause',
    resume: 'Resume',
    actionFailed: 'That did not work. {{detail}}',
    edit: 'Edit server',
    remove: 'Remove server',
    dialog: {
      titleAdd: 'Add a FHIR server',
      titleEdit: 'Edit FHIR server',
      required: 'This is required',
      ownersFailed: 'Who a server can be for could not be read, so none can be added now. Close this and try again.',
      ownersLoading: 'Who a server can be for is still being read. Save again in a moment.',
      noOwners: 'There is no person or organisation a server can be for, so none can be added.',
      addressLocked: 'An address cannot be changed: a server at another address is a new registration.',
      ownerLocked: 'The owner cannot be changed: it decides who can change and remove the server.',
      active: 'Active',
      activeHelp: 'An inactive server is offered to nobody and takes no new connections.',
      saveFailed: 'The server could not be saved.',
    },
    pauseDialog: {
      title: 'Pause this server?',
      message: 'Nothing is read from or written to {{name}} until you resume it. People connected to it see that it is paused. Coach access there is not updated in the meantime.',
      pause: 'Pause',
    },
    removeDialog: {
      title: 'Remove this server?',
      messageDeletes: 'Nobody uses {{name}}, so it is deleted. This cannot be undone.',
      messageDeactivates: '{{name}} is in use, so it is deactivated rather than deleted. Nobody can connect to it while it is inactive. People connected now keep their connection until they end it.',
      remove: 'Remove',
    },
    messages: {
      added: '{{name}} has been added',
      updated: '{{name}} has been saved',
      deactivated: '{{name}} is deactivated',
      activated: '{{name}} is active again',
      paused: '{{name}} is paused',
      resumed: '{{name}} is running again',
      removedDeactivated: '{{name}} is in use, so it was deactivated rather than deleted',
    },
  },
  dataSourcePage: {
    loadFailedTitle: 'This source could not be read',
    loadFailed: 'Try again in a moment.',
    notFoundTitle: 'This source was not found',
    notFoundBody: 'It may have been removed, or it is not offered to you.',
    lastRead: 'Last read {{when}}',
    neverRead: 'Not read yet',
    aboutTitle: 'About this server',
    connectTitle: 'Connect this source',
    connectBody: 'You sign in at {{name}}, and it tells this platform which record is yours. You type nothing in.',
    connect: 'Connect',
    connectRow: 'Connect',
    connectWhenConnected: 'You are connected. Open the source to change or end the connection.',
    connectCannotNameRecord: '{{name}} is set up here without a sign-in, so this platform cannot tell which record is yours.',
    connectNotSetUp: 'Signing in at {{name}} is not set up yet.',
    cannotConnectTitle: 'This source cannot be connected here',
    cannotConnectBody: '{{name}} is set up here without a sign-in. Only a source\'s own sign-in tells this platform which record is yours, so you cannot connect this one.',
    inactiveBody: '{{name}} is not taking new connections at the moment.',
    inactiveConnected: '{{name}} takes no new connections. You can keep using yours, or end it.',
    pausedBody: '{{name}} is paused. Nothing is read from it or written to it until it resumes.',
    pausedConnect: '{{name}} is paused, so it cannot be connected now.',
    stateTips: {
      active: 'This server takes new connections, and what you allow is read and written back as usual.',
      inactive: 'This server takes no new connections. A connection you already have stays until you end it.',
      paused: 'Nothing is read from or written to this server for now. Where a copy is kept here, you still see it.',
    },
    serverStatus: {
      reachable: 'Reachable',
      unreachable: 'Unreachable',
      unread: 'Not read yet',
      notLinked: 'Not linked',
    },
    serverStatusTips: {
      reachable: 'This server answered your last read or test.',
      unreachable: 'This server did not answer your last read or test. Open the source to test it again.',
      unread: 'You are linked, but nothing has been read from this server yet.',
      notLinked: 'You are not linked to this server, so whether it answers is not known.',
    },
    connectionTitle: 'Your connection',
    connectedSince: 'Connected on',
    recordHere: 'Your record at this source',
    keepCopy: 'Keep a copy on this platform',
    keepCopyOn: 'On. Your measurements stay available here when the source is offline.',
    keepCopyOff: 'Off. Measurements are fetched only while you look at them.',
    copyOnToast: 'This platform now keeps a copy of {{name}}.',
    copyOffToast: 'No copy of {{name}} is kept here any more. The copy here was removed.',
    undo: 'Undo',
    undone: 'Undone.',
    nothingToChange: 'There was nothing to change.',
    permissionsTitle: 'What this platform may do with this source',
    summaryNone: 'This platform reads nothing from this source now.',
    summary: 'Reads {{reading}} of {{total}} measurements and writes {{writing}} back.',
    summaryNoWrite: 'Reads {{reading}} of {{total}} measurements and writes nothing back.',
    readEverything: 'Read everything',
    writeNothing: 'Write nothing back',
    columnWriteBack: 'Write back',
    readToggle: 'Read {{name}}',
    writeToggle: 'Write {{name}} back',
    writeNeedsRead: 'Writing back needs reading to be on first.',
    permissionsFootnote: 'Writing back is only possible for measurements this platform may also read. Changes apply at once.',
    state: {
      'read-write': 'Read and written back',
      read: 'Read only',
      write: 'Written back only',
      none: 'No access',
    },
    readOnToast: 'This platform now reads {{name}} from {{source}}.',
    readOffToast: 'This platform no longer reads {{name}} from {{source}}.',
    writeOnToast: '{{name}} is now also written back to {{source}}.',
    writeOffToast: '{{name}} is no longer written back to {{source}}.',
    allReadToast: 'This platform now reads every measurement it may from {{source}}.',
    noWriteToast: 'Nothing is written back to {{source}} any more.',
    viewsLabel: 'Which sources',
    views: {
      all: 'All',
      connected: 'Connected',
      'not-connected': 'Not connected',
    },
    columnSource: 'Source',
    columnAccess: 'Access',
    accessLabel: 'Reads {{reading}} and writes back {{writing}} of {{total}} measurements',
    legendRead: 'Platform may read',
    legendWrite: 'Platform may write back',
  },
  dataProviderOffers: {
    table: {
      tenant: 'Tenant',
      organisation: 'Organisation',
      offeredAt: 'Offered on',
    },
    withdraw: 'Withdraw',
    locked: '{{name}} is shared with the whole platform, so every member sees it. These offers are kept but locked until sharing is turned off on General.',
    lockedReason: 'Locked while the server is shared with the whole platform',
    offered: '{{name}} is offered to {{tenants}}',
    withdrawn: '{{name}} is no longer offered to {{tenant}}',
    withdrawDialog: {
      title: 'Withdraw the offer',
      message: 'Members of {{tenant}} then no longer see {{name}}. Anyone already connected stays connected.',
    },
    add: {
      button: 'Offer to a tenant',
      title: 'Offer {{name}}',
      save: 'Offer',
      organisation: 'Organisation',
      organisationRequired: 'Choose an organisation',
      noOrganisation: 'No organisation found',
      tenant: 'Tenant',
      tenantRequired: 'Choose a tenant',
      allTenants: 'All tenants of {{organisation}}',
      tenantCount_one: '{{count}} tenant',
      tenantCount_other: '{{count}} tenants',
      everyTenant: 'This server is already offered to every tenant.',
      readFailed: 'The tenants could not be read.',
      failed: 'The offer did not go through.',
    },
  },
  dataProviderPage: {
    offers: 'Offered to',
    sharing: {
      title: 'Shared with the whole platform',
      on: 'On: every member of the platform sees this server. Its offers to tenants are kept, and locked while this is on.',
      offOffered: 'Off: only members of the tenants it is offered to see it.',
      personal: 'A server a person owns is theirs alone and is not shared with the whole platform. To share it, register it under an organization.',
      sharedOn: '{{name}} is shared with the whole platform',
      sharedOff: '{{name}} is no longer shared with the whole platform',
      failed: 'The change was not saved.',
    },
    status: 'Status',
    fhirVersion: 'FHIR version',
    canRead: 'Can be read from',
    canWrite: 'Can be written to',
    checkedAt: 'Last checked',
    notChecked: 'Not checked yet',
    yes: 'Yes',
    no: 'No',
    authOauth: 'OAuth 2.0: the member signs in at the provider',
    authServiceAccount: 'A service account of this platform',
    oauthConfigured: 'OAuth set up',
    oauthScopes: 'Scopes it asks for',
    smartScopes: 'Scopes the server offers',
    recordClaim: 'Claim that names the record',
    recordSystem: 'Identifier system of the record',
    notFoundTitle: 'This server could not be found',
    notFoundBody: 'It may have been removed, or the address is wrong.',
    loadFailedTitle: 'This server could not be read',
    loadFailed: 'Try again in a moment.',
  },

  dataPermissions: {
    platformSection: 'This platform',
    platformSectionHelp: 'Which measurements this platform may fetch from each source you have connected, and which it may write back.',
    sourcesUnreadable: 'Your connections could not be read just now, so this list may be incomplete.',
    changeOnCoaches: 'What a coach may see is changed where they are a person rather than a row in a list.',
    goToCoaches: 'Go to my coaches',
    goToProviders: 'Find a data provider',
    nothingSharedTitle: 'Nothing is shared yet',
    nothingSharedBody: 'You have not connected a data provider, and no coach can read your measurements.',
    title: 'Data permissions',
    subtitle: 'Choose which measurements each connected source may read, and how much of them this platform keeps.',
    unnamedSource: 'Unnamed source',

    connectTitle: 'Connect another source',
    connectSubtitle:
      'Any server that speaks FHIR R4 can hold your data. Nothing here is ' +
      'specific to one provider.',
    testAcceptsWrites: 'This provider accepts measurements written to it.',
    testReadOnly:
      'This provider does not accept measurements written to it, so it can only ' +
      'be read from.',

    authorize: 'Authorize at your provider',
    authorizeFailed: 'The authorization could not be started.',
    authorizeNotConfigured:
      '{{provider}} cannot be authorized yet: the provider does not publish ' +
      'where to sign in, and no address has been set for it. An administrator ' +
      'has to arrange that first.',
    alsoRequestWrite:
      'Also ask permission to write measurements to this provider',
    authFailedTitle: 'The authorization did not complete',
    authConnectedTitle: 'Connected to {{provider}}',
    authGrantedReadWrite:
      'Your provider allowed this platform to read and write your measurements.',
    authGrantedReadOnly:
      'Your provider allowed this platform to read your measurements. Writing was ' +
      'not granted.',
    authGrantedNothing:
      'Your provider did not allow this platform to read your measurements, so ' +
      'this connection cannot show anything yet.',
    authNoPatientTitle: 'Almost connected',
    authNoPatientBody:
      '{{provider}} did not identify which record is yours, so the connection ' +
      'could not be finished. Your authorization itself worked -- the provider ' +
      'has to include your record in it, which is not something you can change ' +
      'from this page.',
    authReasons: {
      record_not_named: '{{provider}} did not say which record is yours, so the connection could not be finished automatically.',
      record_refused: '{{provider}} did not accept this authorization for any record. Either it has no record for you yet, or it does not allow this platform to look one up. Whoever manages it can tell you which.',
      record_not_yours: '{{provider}} offered a record that does not carry your identity, so nothing was connected: guessing could show you somebody else\'s measurements.',
      no_record: '{{provider}} has no record for you yet, so there is nothing to connect to. Ask whoever manages it to create your record first.',
      several_records: '{{provider}} offered more than one record for this authorization, so it is not clear which one is yours. Nothing was connected, because guessing could show you somebody else\'s measurements.',
      record_not_served: '{{provider}} did not say which record is yours, and the record its authorization pointed to was not shared, so the connection could not be finished.',
      nothing_served: '{{provider}} accepted the authorization but shared nothing with it, so there is nothing this connection could show.',
      write_unknown: '{{provider}} did not say whether this platform may add measurements to your record, so that is still an open question rather than a refusal. You can switch adding on per measurement; the first one will show whether your provider accepts it.',
      read_not_granted: '{{provider}} did not allow this platform to read your measurements, so this connection cannot show anything yet.',
      holdings_unknown: 'The connection was made, but we could not yet read which measurements {{provider}} holds. Try checking the source again from this page.',
    },

    codesTitle: 'Measurements at this source',
    checkSource: 'Check what this source offers',
    checkSourceHelper:
      'Asks the source which measurements it holds and what it allows. It reads ' +
      'the names of the measurements, not their values, and never switches a ' +
      'measurement back on that you have turned off.',
    codesUnreadable:
      'The measurements for this source could not be read just now, so this ' +
      'list is not showing what you have allowed. Nothing has changed.',
    codesUnreadableRetry: 'Try again',
    nothingOffered:
      'This source has not been checked yet, so no measurements are listed. ' +
      'Check what it offers to see them.',
    nothingFound:
      'This source was checked and returned no measurements for your record, ' +
      'so none are listed.',
    nothingAddressable:
      'This source was checked, but its measurements use a code system this ' +
      'platform cannot request, so none are listed.',
    allOff:
      'Every measurement here is switched off, so nothing is read from this source.',
    unmappableTitle: 'Some measurements cannot be interpreted here',
    unmappableBody: 'This platform has no measurement for {{count}} of the codes below, so their values cannot be shown even though you have allowed them. Each one is marked in the list; they are kept aside with the reason rather than dropped.',
    statedByYou: 'Added by you; not confirmed by the source',

    columnMeasurement: 'Measurement',
    columnAccess: 'What happens',
    columnRead: 'Read',
    columnWrite: 'Write',

    access: {
      none: 'Nothing',
      read: 'Read only',
      write: 'Write only',
      'read-write': 'Read and write',
    },

    reasonYouTurnedItOff: 'You have switched this off.',
    reasonSourceRefusesRead: 'This source does not allow reading.',
    reasonSourceRefusesWrite:
      'This source does not accept measurements written to it.',
    reasonSourceNotChecked:
      'It is not known whether this source accepts writing. Check what it ' +
      'offers first.',
    reasonUnknown: 'This is switched off.',

    probeAdded: '{{count}} new measurement(s) found.',
    probeNothingNew: 'No new measurements found.',
    probeConfirmed: '{{count}} already known.',
    probeUnaddressable:
      '{{count}} measurement(s) use a code system this platform cannot request.',

    addCode: 'Add a LOINC code',
    addCodeHelper:
      'Only needed for a measurement this source will hold but does not hold ' +
      'yet. For example 29463-7.',
    add: 'Add',
    invalidCode: 'That is not a LOINC code. A LOINC code looks like 29463-7.',
    addFailed: 'The code could not be added.',
    updateFailed: 'The permission could not be changed.',
    probeFailed: 'The source could not be checked.',
    scopeSaved: '{{name}}: {{state}}.',
    codeAdded: '{{code}} added.',
    writeMovedFrom:
      'Writing was still switched on at {{source}}, where it could not do ' +
      'anything. That is off now: one source writes a measurement.',

    columnAvailable: 'In this platform',
    availableHere: 'Available',
    notAvailableHere: 'Not available',
    notAvailableHelp: 'This platform has no measurement for this code, so values under it cannot be shown even when you allow them.',
    unknownCode: 'Not a code this platform recognizes',
    nothingOfferedEnded: 'No measurements were recorded for this connection before it ended.',
    providerSearchPlaceholder: 'Search by name or address',
    providerSearchHelper:
      'Type part of the name. Providers your organization has an agreement ' +
      'with, plus any server you registered yourself.',
    providerMoreMatches: '{{count}} more match what you typed — keep typing to narrow it down.',
    providerNoMatches: 'No provider or server matches that.',
    yourServer: 'Registered by you',
    addServer: 'Add a FHIR server',
    addServerTitle: 'Add a FHIR server',
    addServerBody:
      'Any server that speaks FHIR R4 can hold measurements. Who it is for ' +
      'decides who can see it and who can remove it. This platform makes no ' +
      'claim about the server itself, so check the address before saving.',
    serverName: 'What to call it',
    serverNameHelp: 'The name whoever can see this server will read.',
    serverAddress: 'Address of the FHIR server',
    serverAddressHelp:
      'The base address, the one /metadata and /Observation sit under.',
    serverAuth: 'How it checks who is asking',
    serverAuthHelp:
      'Pick what your server expects. Test the connection afterwards to be sure.',
    authNone: 'No credential needed',
    authBearer: 'A token',
    authBasic: 'A username and password',
    authOauth: 'Signing in at the server (OAuth 2.0)',
    serverToken: 'The token',
    serverUsername: 'Username',
    serverUsernameHelp:
      'The name your server expects, exactly as you would type it to sign in ' +
      'there yourself.',
    serverPassword: 'Password',
    usernameHasColon:
      'A username cannot contain a colon. The colon is what separates the ' +
      'username from the password, so everything after it would be sent as ' +
      'part of the password.',
    serverSecretHelp:
      'Encrypted before it is stored and never shown again, here or anywhere ' +
      'else. It is only used to make the request to your own server.',
    serverReturnAddress: 'Return address',
    serverReturnAddressHelp: 'Register this address at the server for the client it gives this platform. The server sends you back here after you sign in.',
    copyReturnAddress: 'Copy',
    returnAddressCopied: 'Return address copied',
    returnAddressCopyFailed: 'The return address could not be copied. Select it and copy it yourself.',
    serverClientId: 'Client id',
    serverClientIdHelp: 'The id the server gave this platform when it was registered there.',
    serverClientSecret: 'Client secret',
    serverClientSecretHelp: 'Only if the server gave one. Encrypted before it is stored and never shown again.',
    serverRecordClaim: 'Claim that names your record',
    serverRecordClaimHelp: 'Only for a server whose sign-in carries an identifier instead of your record. Leave this and the next field empty otherwise.',
    serverRecordSystem: 'Identifier system',
    serverRecordSystemHelp: 'The system that identifier is registered under at the server.',
    recordPairIncomplete: 'Fill in both fields, or neither.',
    testAddress: 'Test this address',
    testUsable: 'Answers as FHIR {{version}}. {{software}}',
    testNotFhir: 'Something answered, but not as a FHIR R4 server.',
    testMissingResources:
      'This is a FHIR R4 server, but it does not offer the records and measurements this platform reads.',
    testUnreachable: 'Nothing answered at that address.',
    testFailed: 'The address could not be tested.',
    columnName: 'Name',
    registerServer: 'Save server',
    ownServerRegistered: '{{name}} has been added. Connect to it below.',
    ownServerFailed: 'That server could not be added.',
    startsKeepingNothing:
      'A new connection keeps nothing on this platform: your measurements are ' +
      'fetched from your provider while you are looking at them. You can let ' +
      'this platform keep a copy afterwards, on the connection itself.',
    yourSources: 'Your sources ({{count}})',
    searchSources: 'Search your sources',
    noSourcesMatch: 'None of your sources matches “{{query}}”.',
  },
  remoteStorage: {
    storedHere: 'Stored on this platform',
    storedHereNotice: 'This platform stores your measurements and is responsible for keeping them. You can move them to your own care provider below.',
    sourcesUnknown: 'Your connections could not be loaded',
    sourcesUnknownNotice: 'We could not check where your measurements are being kept. Please refresh the page, and change nothing here for now: this screen cannot currently show which connections are active.',
    provider: 'Care provider',
    copyExpiry: 'Keep a copy for',
    expiryIndefinite: 'Indefinitely',
    testConnection: 'Test connection',
    testUsable: 'Reached {{software}} speaking FHIR {{version}}. This provider can be used.',
    testFailed: 'Could not reach that provider',

    testThisSource: 'Test this source',
    healthWorking: 'Answering',
    healthWorkingHelp:
      'The last time this platform read from this source, on {{when}}, it ' +
      'answered. Use “Test this source” to check it again now.',
    healthFailing: 'Not answering',
    healthFailingHelp:
      'The last attempt to read from this source, on {{when}}, did not ' +
      'succeed: {{detail}}',
    healthUntried: 'Not used yet',
    healthUntriedHelp:
      'Nothing has been read from this source yet, so there is nothing to say ' +
      'about whether it answers. Use “Test this source” to find out.',
    checkOk: '{{software}} answered, accepted this platform, and still holds your record.',
    checkCredentialRefused:
      'This source answered but refused the credential stored for it, so nothing ' +
      'can be read from it. If you registered this server yourself, add it again ' +
      'with a credential that works.',
    checkUnreachable: 'This source could not be reached: {{detail}}',
    checkRecordMissing:
      'This source answered, but it no longer has the record this connection ' +
      'points at. Nothing can be read until it points at a record that exists.',
    checkNotUsable: 'This source answered, but not in a way this platform can read: {{detail}}',
    checkFailed: 'The check could not be run.',
    checkedWorking: 'This source is working.',
    checkedNeedsAttention: 'This source needs a look.',
    checkedWentWrong: 'Something went wrong with this source.',
    connect: 'Connect',
    invalidExpiry: 'Choose how long a copy may be kept',
    retentionUpdateFailed: 'Could not change your retention setting',
    syncNow: 'Fetch now',
    syncFailed: 'Could not fetch from your provider',
    inconsistentTitle: 'This does not look right',
    inconsistentBody: 'You asked us to keep no copy of your data, but {{count}} measurement(s) are still stored here. Please report this.',
    endConnection: 'End connection',
    endConnectionTitle: 'End this connection?',
    endConnectionBody: 'Your connection will be closed and every copy of your data on this platform will be deleted. Your provider keeps its own record unless you ask us to delete it there too.',
    alsoDeleteAtProvider: 'Also ask my provider to delete what this platform wrote there',
    confirmEnd: 'End connection',
    revokeFailed: 'Could not end the connection',
    cancel: 'Cancel',
    endedConnections: 'Connections you have ended',
    endedOn: 'Ended {{when}}',

    modes: {
      zeroRetention: {
        label: 'Keep nothing here',
        benefit:
          'Your health data never rests on this platform. Ending the connection leaves no content behind, because there was none.',
        cost:
          'Screens are slower, and when your provider cannot be reached your data is not available at all.',
      },
      cache: {
        label: 'Keep a cached copy',
        benefit:
          'Screens are fast and your data stays visible when your provider is unreachable.',
        cost:
          'A copy of your data rests on this platform for as long as you choose, and is covered by this platform\'s security rather than only your provider\'s.',
      },
      local: {
        label: 'Store it on this platform',
        benefit:
          'Everything works as it does today, with full integrity checks across your records.',
        cost:
          'This platform holds your health data and is responsible for keeping it. Chosen by having no connection to a provider.',
      },
    },

    noticeCache:
      'Your observations are stored by your provider. This platform keeps a copy for up to {{expiry, minutes}} so screens stay fast and remain available if the provider is down.',
    noticeCacheIndefinite:
      'Your observations are stored by your provider. This platform keeps a copy until you end the connection, so screens stay fast and remain available if the provider is down. The copy is refreshed when you fetch or record something, not on its own.',
    noticeZeroRetention:
      'Your observations are stored by your provider. This platform keeps no copy: data is fetched while you are looking at it and is not retained.',

    syncRead: 'Read {{count}} measurement(s) from your provider.',
    syncUnchangedAll: 'Nothing had changed.',
    syncChanged: '{{written}} new, {{replaced}} corrected, {{unchanged}} unchanged.',
    syncQuarantined:
      '{{count}} could not be interpreted and are not shown. Your data ' +
      'settings page names them.',
    revokeNothingChanged: 'The connection could not be ended. Nothing has changed.',
    revokePurged: 'Connection ended and {{count}} stored measurement(s) deleted here.',
    revokeRemoteConfirmed: 'Your provider confirmed it deleted what we had written there.',
    revokeRemoteFailed: 'Your data is still at your provider: {{detail}}',

    ttlNotApplicable:
      'An expiry does not apply to this mode: nothing is stored, so nothing expires.',
    ttlNotWhole: 'The expiry must be a whole number of minutes.',
    ttlOutOfRange:
      'The expiry must be between {{min}} minutes and 7 days, or choose that the copy does not expire.',
  },
  recordPage: {
    path: 'Path',
    general: 'General',
  },
  programmePage: {
    measurements: 'Measurements',
    dashboards: 'Dashboard',
    notFoundTitle: 'This programme could not be found',
    notFoundBody: 'It may have been removed, or it belongs to another organization.',
    loadFailedTitle: 'This programme could not be read',
    loadFailed: 'Try again in a moment.',
    people: 'Participants',
  },
  programmeBoards: {
    loading: 'Loading the dashboard',
    panelTitle: 'This programme’s dashboard',
    panelHint: 'Every member of this programme finds it on their dashboard once it has a block. A save reaches them at once.',
    name: 'Name',
    nameHint: 'Left empty, it takes the programme’s name.',
    unsaved: 'Not saved',
    save: 'Save',
    discard: 'Discard changes',
    saved: 'Saved. Members of this programme see it now.',
    saveFailed: 'The dashboard could not be saved. Try again.',
    emptyTitle: 'This dashboard is still empty',
    emptyBody: 'Pick a block above. Members see the dashboard once it has one.',
    deniedTitle: 'You cannot change this programme’s dashboard',
    deniedBody: 'That takes the right to configure this programme.',
    loadFailedTitle: 'The dashboard could not be read',
    retry: 'Try again',
    galleryHint: 'Pick a block to put it at the bottom of this dashboard. Each block shows one of this programme’s measurements.',
    arrangeHint: 'Drag a block by its handle, or pull its bottom-right corner. Each member sees their own values in it.',
    noMeasurements: 'This programme tracks no measurement yet. Add one on the Measurements tab first.',
  },
  questionnairePage: {
    questions: 'Questions',
    submissions: 'Submissions',
    notFoundTitle: 'This questionnaire could not be found',
    notFoundBody: 'It may have been removed, or it belongs to another organization.',
    loadFailedTitle: 'This questionnaire could not be read',
    loadFailed: 'Try again in a moment.',
  },
  tenantPage: {
    administrators: 'Administrators',
    notFoundTitle: 'This tenant could not be found',
    notFoundBody: 'It may have been removed, or the address is wrong.',
    loadFailedTitle: 'This tenant could not be read',
    loadFailed: 'Try again in a moment.',
    table: {
      name: 'Name',
      email: 'Email',
    },
    addButton: 'Add administrator',
    remove: 'Remove',
    removeTitle: 'Remove administrator',
    removeMessage: '{{name}} will no longer administer {{tenant}}. Their account and their membership stay.',
    removed: 'Administrator removed',
    added: 'Administrator added',
    add: {
      title: 'Add an administrator to {{tenant}}',
      person: 'Person',
      personRequired: 'Choose a person',
      nobody: 'Nobody in this organization can be chosen.',
      peopleFailed: 'The people of this organization could not be read.',
      save: 'Add',
      failed: 'The administrator could not be added.',
    },
  },
  grid: {
    noRowsTitle: 'Nothing here yet',
    noRowsNotice: 'Rows appear as soon as there is something to show.',
    noResultsTitle: 'Nothing matches that',
    noResultsNotice: 'Try a shorter search, or clear the filters.',
    actions: 'Actions',
    moreActions: 'More actions',
    edit: 'Edit',
    remove: 'Remove',
    clearSearch: 'Clear search',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    actionFailed: 'That did not work, and nothing was changed.',
    loadFailedTitle: 'This list could not be read',
    add: 'Add',
    filter: 'Filter',
    searchAll: 'Search all columns',
    sortByField: 'Sort by: {{field}}',
    sortByFieldDirection: 'Sort by: {{field}}, {{direction}}',
    filterWithCount: 'Filter ({{count}})',
    newestFirst: 'Newest first',
    noResultsFor: 'No results for “{{query}}”',
    clearSearchAndFilters: 'Clear search & filters',
    tryAgain: 'Try again',
    rowsPerPage: 'Rows per page',
    range: '{{from}}–{{to}} of {{count}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
  },
  coaches: {
    title: 'Coaches',
    subtitle:
      'The coaches your organization offers. You choose who to ask, and what ' +
      'they may see of your measurements.',
    coachingSubtitle: 'The people you coach, the requests made to you, and how the directory shows you.',
    unnamedCoach: 'Unnamed coach',
    unnamedMember: 'Unnamed member',

    requestPendingChip: 'Request sent',
    ask: 'Ask this coach',
    details: 'Details',
    withdrawRequest: 'Withdraw request',
    access: {
      held: 'Sees {{see}} of your measurements and can record {{record}}.',
      asked: 'Asks to see {{see}} measurements and to record {{record}}.',
    },
    cannotAsk: {
      alreadyMyCoach: 'This coach already sees your measurements.',
      requestPending: 'Your request is still waiting for an answer.',
      notAccepting: 'This coach is not taking on new people at the moment.',
    },

    askTitle: 'Ask {{name}}',
    askExplainer:
      'Choose what this coach may see. You can change it, or end it, at any ' +
      'time under your coaches.',
    measurement: 'Measurement',
    maySee: 'May see',
    mayRecord: 'May record',
    maySeeAria: 'May see {{name}}',
    mayRecordAria: 'May record {{name}}',
    messageLabel: 'Message (optional)',
    sendRequest: 'Send request',
    askFailed: 'The request could not be sent.',
    scopesUnavailable: 'Your programme\'s measurements could not be read.',
    noProgrammeYet:
      'You are not following a program yet, and what can be shared comes from the program.',
    someUnsharable:
      'Not shareable yet: {{names}}. These measurements have no LOINC code — ask an administrator to bind one.',
    noMeasurementsYet:
      'Your program names no measurements yet, so there is nothing to share.',

    waitingForYou: 'Waiting for your answer',
    stillWaiting: 'Still waiting for an answer',
    earlierRequests: 'Earlier requests',
    offeredAccess: 'What they asked for',
    sharedProgrammes: 'Programmes you share',
    askedOn: 'Asked on {{when}}',
    answerableUntil: 'Can be answered until {{when}}',
    expiredOn: 'Expired on {{when}}',
    expired: 'Expired',
    accept: 'Accept',
    decline: 'Decline',
    declineTitle: 'Decline request',
    declineReasonLabel: 'Reason (optional)',
    actionFailed: 'That did not work.',
    status: {
      pending: 'Waiting for an answer',
      accepted: 'Accepted',
      declined: 'Declined',
      withdrawn: 'Withdrawn',
      expired: 'Expired',
    },

    myListing: 'Your coach listing',
    myListingExplainer:
      'Only you put this listing in the directory. It is separate from your ' +
      'community sharing preference.',
    listingSaved: 'Coach profile saved.',
    saveFailed: 'The listing could not be saved.',
    headlineLabel: 'Headline',
    bioLabel: 'About you',
    programmesLabel: 'Programmes you coach',
    maxMembersLabel: 'Maximum number of people',
    maxMembersHelp_one: 'You currently coach {{count}} person.',
    maxMembersHelp_other: 'You currently coach {{count}} people.',
    acceptingLabel: 'Open to new people',
    publishLabel: 'Advertise in my organization',
    publishNeedsBoth:
      'A listed profile needs a headline and something about you: that is what ' +
      'someone chooses on.',

    peopleYouFollow: 'People you coach',
    peopleYouFollowExplainer:
      'What you may see of someone is theirs to decide.',

    directoryUnknown: 'The directory could not be read',
    directoryUnknownNotice: 'Please try again later.',
    onlyYouListedTitle: 'You are the only coach listed',
    onlyYouListedNotice:
      'No other coach in your organization has published a listing yet.',
    noneListedTitle: 'No coaches listed yet',
    noneListedNotice:
      'As soon as a coach in your organization publishes a listing, they appear here.',
    canAsk: 'Coaches you can ask',
    allAlreadyYoursTitle: 'No other coaches yet',
    allAlreadyYoursNotice: 'The coaches who already coach you are listed above. Anyone else in your organization who publishes a listing appears here.',
    onlyAccepting: 'Only those taking on new people',
    search: 'Search for a coach',
    noneAcceptingTitle: 'Nobody is taking on new people',
    noneAcceptingNotice: 'None of the listed coaches is open to new people right now.',
    listingUnknownTitle: 'Your coach profile could not be read',
    listingUnknown: 'What you see here may be out of date.',
    noListingTitle: 'You do not have a coach listing',
    noListing:
      'This screen is where a coach edits the profile members see. Your account holds no coach role, so there is nothing here to edit yet.',
    grantsUnknownTitle: 'Your coaches could not be read',
    grantsUnknown: 'What you see here may be out of date.',
    noneMatchTitle: 'No coach found',
    noneMatchNotice: 'Nothing matches "{{query}}". Try part of a name or a programme.',

    myCoaches: 'Your coaches',
    changeOnCoaches:
      'Change which measurements a coach may see on the coaches page.',
    whatTheyMaySee: 'What they may see',
    sinceExplainer: 'You gave this access on {{when}}. Changes apply at once.',
    saveAccess: 'Save changes',
    accessSaved: 'Changes saved.',
    accessSaveFailed: 'The change could not be saved.',
    keepOneOrEnd:
      'Keep at least one measurement, or end the access instead — that way the coach is told.',
    nothingShared: 'Nothing is shared with this coach.',
    accessEndedOn: 'This access ended on {{when}}.',
    offerUnreadable:
      'Your programme\'s measurements could not be read, so only what is already shared is listed.',
    endCoaching: 'End access',
    endCoachingTitle: 'End access',
    endCoachingBody:
      '{{name}} will no longer be able to see your measurements. What is ' +
      'already recorded stays.',
    grantedOn: 'Access given on {{when}}',
    endedCoaching: 'Ended coaching',
    endedOn: 'Ended on {{when}}',
    revokeFailed: 'Access could not be ended.',

    tabs: {
      coaches: 'My coaches',
      history: 'History',
      listing: 'My coach profile',
      requests: 'Requests to me',
      members: 'People I coach',
    },

    noProgrammeTitle: 'You are not following a programme yet',
    noProgrammeNotice: 'A coach guides a programme. Once you join one, the coaches who guide it appear here.',
    noHistoryTitle: 'Nothing to look back on',
    noHistoryNotice: 'Coaching that ended, and requests that came to nothing, are kept here.',
    peopleYouCoached: 'People you have coached',
    noPastMembersTitle: 'Nobody has stopped',
    noPastMembersNotice: 'Everyone you coach is still with you. Coaching that ends is recorded here.',
    noOpenRequestsTitle: 'No requests waiting',
    noOpenRequestsNotice: 'When someone asks you to coach them, their request appears here.',
    answeredRequests: 'Answered requests',
    noMembersYetTitle: 'You are not coaching anybody yet',
    noMembersYetNotice: 'When someone accepts your request, they appear here with what they let you see.',
    memberUnreadableTitle: 'These measurements could not be read',
    memberUnreadable: 'Please try again in a moment.',
    nothingPermittedTitle: 'Nothing to show',
    nothingPermittedNotice: 'This person\'s programmes name no measurements that could be shared with you.',
    nothingSharedWithYouTitle: 'Nothing shared yet',
    nothingSharedWithYouNotice: 'This person has not shared any measurements with you. What they could share is named below.',
    notShared: 'Not shared with you',
    notSharedNotice: 'This person did not share this measurement, so nothing about it is shown.',
    sourceUnavailable: 'Source did not answer',
    sourceIncomplete: 'Partly unreadable',
    sourceIncompleteNotice: 'Some values this source sent could not be read, so what is shown here is not all of them.',
    sourceSilent: 'The source holding these values did not answer',
    sourceRefused: 'Source not readable for you',
    sourceNotReadForYou: 'The source holding these values cannot be read on your behalf',
    recordedHere: 'Recorded on this platform',
    fromSourceAsOf: 'From {{source}}, checked {{when}}',
    asOfUnknown: 'at an unknown time',
    mixedSources: 'Values come from {{places}} different places, so no change is shown.',
    sourcesDegradedTitle: 'Some values may be missing',
    sourcesDegraded: '{{names}} did not answer. Values held there may be missing from this screen.',
    sourcesRefusedTitle: 'Some values cannot be shown',
    sourcesRefused: '{{names}} cannot be read on your behalf, so values held there are not on this screen. This is about access between that source and this platform, not about what this person shared with you.',
    sourceNotConnectedChip: 'Not connected',
    sourceNotConnectedCaption: 'Connect your own account to read the values held at this source',
    sourcesNotConnectedTitle: 'Connect to read some values',
    sourceNotConnected: '{{name}} can only be read with your own account. Connect it once, and the values this person shared with you can be shown here.',
    sourceReconnect: 'Your connection to {{name}} has ended, so values held there are not on this screen. Connect again to read what this person shared with you.',
    connectSource: 'Connect {{name}}',
    reconnectSource: 'Connect {{name}} again',
    connectedTitle: 'Connected to {{source}}',
    connectedBody: 'Open this person again to read the values that were shared.',
    connectFailedTitle: 'The connection could not be made',
    connectFailedBody: 'Nothing was changed. Try again later.',
    notConnectedTitle: 'Not connected to {{source}}',
    connectReasons: {
      refused: '{{source}} did not accept the sign-in, so nothing was connected.',
      no_code: 'The sign-in at {{source}} returned nothing, so nothing was connected.',
      no_party_id: 'Your account at {{source}} carries no JLAM ID yet, so it cannot name you there. Ask whoever manages accounts.',
      no_person: 'Your account here is not linked to a person, so it cannot be connected.',
    },
    sourcesStaleTitle: 'Some values may be out of date',
    sourcesStale: 'We could not refresh {{names}}. Anything recorded there since we last checked is missing from this screen.',
    withheldFromHistory: 'Not shared with you: {{names}}',
    source: 'Source',
    asOf: 'Checked',
    here: 'This platform',
    lastRecorded: 'Last recorded on {{when}}',
    lastRecordedByYou: 'Last recorded by you on {{when}}',
    noReadingYet: 'No value recorded yet',
    readOnlyHere: 'View only',
    newValue: 'New value',
    unit: 'Unit',
    measuredAt: 'Measured at',
    momentInFuture: 'A measurement cannot be dated in the future.',
    recordedSome_one: '{{count}} value recorded; the rest could not be.',
    recordedSome_other: '{{count}} values recorded; the rest could not be.',
    record: 'Record',
    recorded_one: '{{count}} value recorded.',
    recorded_other: '{{count}} values recorded.',
    recordFailed: 'The value could not be recorded.',
    nothingToRecord: 'Fill in a value first.',
    valueNotANumber: 'This measurement expects a number.',
    requestSent: 'Request sent to {{name}}.',
    requestAccepted: 'You now coach {{name}}.',
    requestDeclined: 'Request from {{name}} declined.',
    requestWithdrawn: 'Request to {{name}} withdrawn.',
    accessEnded: '{{name}} can no longer see your measurements.',
    findSomeoneYouCoach: 'Find someone you coach',
    personColumn: 'Person',
    sinceColumn: 'Since',
    trendColumn: 'Trend',
    lastActivityColumn: 'Last recorded',
    lastActivityNever: 'Nothing recorded',
    lastActivityOn: 'Last recorded on {{when}}',
    searchMembers: 'Search the people you coach',
    noMatchesTitle: 'Nobody matches that',
    noMatchesNotice: 'Try a shorter search, or clear the box.',
    openMember: 'Open {{name}}',
    caseloadUnknownTitle: 'The people you coach could not be read',
    caseloadUnknownNotice: 'Please try again in a moment.',
    jumpToAnother: 'Go to another person',
    memberNotInCaseloadTitle: 'Not someone you coach',
    memberNotInCaseloadNotice: 'This person is not in your list. The coaching may have ended, or the link may be old.',
    trendNothing: 'Nothing shared',
    trendInRange_one: '{{count}} in range',
    trendInRange_other: '{{count}} in range',
    trendToward_one: '{{count}} moving toward target',
    trendToward_other: '{{count}} moving toward target',
    trendAway_one: '{{count}} moving away from target',
    trendAway_other: '{{count}} moving away from target',
    trendTracked_one: '{{count}} tracked',
    trendTracked_other: '{{count}} tracked',
    coachingSince: 'Coaching since {{when}}',
    viewTrends: 'Trends',
    viewHistory: 'History',
    recordedAt: 'Recorded',
    value: 'Value',
    recordedBy: 'Recorded by',
    you: 'You',
    noReadingsTitle: 'Nothing recorded yet',
    noReadingsNotice: 'Values recorded for this person appear here, newest first.',
  },
  manageRoles: {
    noTenantNotice: 'Roles belong to an organization, and this account belongs to none.',
    noTenantTitle: 'No organization to show roles for',
  },
  rolePermissions: {
    label: 'Pages and actions per role',
    page: 'Page',
    pageAction: 'Page / action',
    rolePicker: 'Role',
    allRoles: 'All roles',
    search: 'Search pages, actions or routes',
    filter: 'Show',
    filterAll: 'All',
    notEnforced: 'Not enforced yet',
    notEnforcedHint: 'The API does not check this action yet. You are recording what should happen; it has no effect today.',
    destructive: 'grant here',
    destructiveHint: 'This action removes access or hands out rights, so it never arrives from a base role. You switch it on deliberately, per role.',
    alwaysAvailable: 'Always available',
    controlLabel: '{{role}}: {{item}}',
    pageOff: '{{page}} is off for {{role}}',
    notOffered: 'Not offered to {{role}}',
    noResultsTitle: 'No permissions match “{{query}}”',
    noResultsFilterTitle: 'No permissions match this filter',
    noResultsBody: 'Check the spelling or switch the filter to All.',
    clear: 'Clear search & filter',
    changes: '{{changes}} across {{roles}}',
    changeCount_one: '{{count}} unsaved change',
    changeCount_other: '{{count}} unsaved changes',
    changesShort: '{{changes}} · {{roles}}',
    changeCountShort_one: '{{count}} change',
    changeCountShort_other: '{{count}} changes',
    roleCount_one: '{{count}} role',
    roleCount_other: '{{count}} roles',
    allSaved: 'All changes saved',
    discard: 'Discard',
    save: 'Save changes',
    saveShort: 'Save',
    saving: 'Saving…',
    saved: 'Permissions saved',
    savedEverywhere: 'Permissions saved; every organization that follows these roles has them now',
    saveFailed: '{{role}} was not saved: {{reason}}',
    saveFailedReason: 'the server refused it',
    loadFailedTitle: 'The roles could not be read',
    members_one: '{{count}} member',
    members_other: '{{count}} members',
    organisations_one: '{{count}} organization',
    organisations_other: '{{count}} organizations',
    reachNotice: 'What you change here applies to every organization that has not decided otherwise about that page or action.',
    grantedTo: 'Granted to {{roles}}',
    grantedToNone: 'Granted to no role',
    sheetPageContext: '{{group}} · page {{route}}',
    sheetActionContext: '{{group}} · {{page}}',
    pageVisible: 'Page visible',
    pageHidden: 'Page hidden',
    pageOffForRole: '{{page}} page is off for this role',
    done: 'Done',
    sections: {
      member: 'Member pages',
      manage: 'Management',
      account: 'Account',
      admin: 'Administration',
    },
  },
  roleList: {
    add: 'Add role',
    edit: 'Edit role',
    remove: 'Remove role',
    columns: {
      name: 'Name',
      heldBy: 'Held by',
      inheritsFrom: 'Inherits from',
      description: 'Description',
      ownChanges: 'Own changes',
      pages: 'Pages',
      actions: 'Actions',
      kind: 'Kind',
    },
    people_one: '{{count}} person',
    people_other: '{{count}} people',
    ownRole: 'Its own',
    followsBase: 'Follows its base',
    ownChanges_one: '{{count}} own change',
    ownChanges_other: '{{count}} own changes',
    platformRole: 'Platform role',
    organisationRole: 'Organization role',
    platformNameFixed: 'The platform looks this role up by name, so its name and description stay as they are. What it may do is changed on Role permissions.',
    platformRoleKept: 'The platform looks this role up by name, so it cannot be removed.',
    heldRoleKept_one: '{{count}} person holds this role. Give them another role before removing it.',
    heldRoleKept_other: '{{count}} people hold this role. Give them another role before removing it.',
    saveError: 'The role could not be saved.',
    dialog: {
      titleAdd: 'Add role',
      titleEdit: 'Edit role',
      name: 'Name',
      description: 'Description',
      nameRequired: 'A role needs a name.',
      startFrom: 'Start from',
      startEmpty: 'Start empty',
      startFromHint: 'Copies that role\'s pages and actions as a starting point. Nothing a new role may not hold is copied.',
    },
    removeDialog: {
      title: 'Remove role',
      message: 'Remove {{name}}? Its pages and actions go with it. Nobody holds it.',
      remove: 'Remove',
    },
    messages: {
      added: '{{name}} added',
      updated: '{{name}} saved',
      removed: '{{name}} removed',
    },
  },
  systemRoleList: {
    subtitle: 'The roles every organization\'s roles inherit from.',
    add: 'Add role',
    edit: 'Edit role',
    remove: 'Remove role',
    columns: {
      name: 'Name',
      heldBy: 'Held by',
      enforced: 'Enforced',
      extends: 'Extends',
      description: 'Description',
    },
    everyOrganisation: 'Every organization',
    notEnforced: 'Not enforced',
    root: 'Base of all roles',
    enforcedKept: 'Every organization has this role, so it cannot be removed.',
    baseKept: '{{names}} extend this role, so it cannot be removed.',
    heldKept_one: '{{count}} organization\'s role inherits from this one, so it cannot be removed.',
    heldKept_other: '{{count}} organizations\' roles inherit from this one, so it cannot be removed.',
    saveError: 'The role could not be saved.',
    dialog: {
      titleAdd: 'Add system role',
      titleEdit: 'Edit system role',
      name: 'Name',
      nameRequired: 'A role needs a name with letters or digits.',
      nameLocked: 'Organizations know this role by its name, so it stays.',
      description: 'Description',
      extends: 'Extends',
      extendsHint: 'It can be given what this role holds, and nothing more.',
      extendsRequired: 'Choose the role it extends.',
      extendsLocked: 'What it extends is set when it is added.',
      everywhere: 'Every organization gets this role as soon as you save it, holding no pages until you switch them on. It cannot be removed afterwards.',
    },
    removeDialog: {
      title: 'Remove system role',
      message: 'Remove {{name}}? No organization holds it and no role extends it.',
      remove: 'Remove',
    },
    messages: {
      added: '{{name}} added to every organization',
      updated: '{{name}} saved',
      removed: '{{name}} removed',
    },
  },
  globalRoleList: {
    subtitle: 'The roles of the platform\'s own administration.',
    columns: {
      name: 'Name',
      heldBy: 'Held by',
      screens: 'Screens',
    },
    screens: '{{held}} of {{total}} screens',
  },
  look: {
    title: 'Symbol and colors',
    symbol: {
      title: 'Symbol',
      description: 'Shown to every member beside the tenant name in the navigation bar. It belongs to the organization, so each of its tenants shows the same symbol.',
      preview: 'How the symbol sits in the navigation bar',
      none: 'No symbol yet. Members see the tenant name on its own.',
      accepted: 'PNG or WebP, at most 512 KB and at least 64 × 64 pixels.',
      choose: 'Choose an image',
      save: 'Save symbol',
      saved: 'Symbol saved.',
      remove: 'Remove symbol',
      removed: 'Symbol removed.',
      refused: 'This file cannot be used. Choose a PNG or WebP image of at most 512 KB and at least 64 × 64 pixels.',
      error: 'The symbol could not be saved. Try again.',
    },
    profiles: {
      title: 'Color profiles',
      description: 'Members see the default profile until they pick one of their own. The MLP standard profiles are always offered next to your own.',
      standard: 'MLP standard profile',
      own: 'Profile of this tenant',
      default: 'Default',
      makeDefault: 'Make default',
      defaultSaved: '{{name}} is now the default profile.',
      edit: 'Edit colors',
      history: 'History',
      add: 'Add a profile',
      newName: 'Name of the profile',
      create: 'Save profile',
      added: '{{name}} was added as version 1.',
      duplicateName: 'This tenant already has a profile with that name. Choose another name.',
      loadError: 'The color profiles could not be loaded. Reload the page to try again.',
      saveError: 'The change could not be saved. Try again.',
    },
    editor: {
      title: 'Colors of {{name}}',
      newTitle: 'New color profile',
      hexLabel: 'Hex value of {{role}}',
      variants: 'Lighter, main and darker variant, with the text color on the main one',
      textSample: 'Aa',
      contrastIntro: 'Text would be hard to read with these colors. Change the color named below:',
      contrastFailure: '{{role}}, {{pairing}}: {{ratio}}:1, where at least {{required}}:1 is needed',
      pairing: {
        textOnColour: 'text on this color',
        linkOnPaper: 'as link text on white',
        linkOnPage: 'as link text on the page background',
      },
      save: 'Save as a new version',
      saved: 'Saved as version {{version}}.',
      reset: 'Reset to Meridian',
      close: 'Close',
    },
    history: {
      title: 'History of {{name}}',
      version: 'Version {{version}}',
      savedBy: '{{when}}, by {{who}}',
      savedAt: '{{when}}',
      current: 'In effect',
      rollBack: 'Roll back to this version',
      rolledBack: 'Rolled back. Saved as version {{version}}.',
      loadError: 'The history could not be loaded.',
      rollBackError: 'The rollback could not be saved. Try again.',
    },
    preview: {
      title: 'Preview',
      navigationCurrent: 'Dashboard',
      navigationOther: 'Measurements',
      button: 'Save',
      secondButton: 'Add',
      link: 'A link',
      chart: 'A measurement over a week, with its target range',
    },
    roles: {
      primary: {
        name: 'Main color',
        hint: 'Navigation, buttons, links and focus',
      },
      secondary: {
        name: 'Second color',
        hint: 'Second buttons and highlights',
      },
      success: {
        name: 'In range',
        hint: 'A value where it should be, and what worked',
      },
      warning: {
        name: 'Off track',
        hint: 'A value away from its target',
      },
      error: {
        name: 'Alert',
        hint: 'Errors, and what needs attention now',
      },
      info: {
        name: 'Information',
        hint: 'Neutral notices',
      },
    },
    standard: {
      meridian: 'Meridian',
      lagune: 'Lagoon',
      classic: 'Classic',
    },
    choice: {
      title: 'Color profile',
      description: 'How the app looks to you. Your choice applies once you save your profile, and follows you to other devices.',
      followTenant: 'Default of {{tenant}} ({{name}})',
      followMeridian: 'MLP default (Meridian)',
      loadError: 'The color profiles could not be loaded.',
    },
  },
  organisationPage: {
    people: 'People',
    notFoundTitle: 'This organization could not be found',
    notFoundBody: 'It may have been removed, or the address is wrong.',
    loadFailedTitle: 'This organization could not be read',
    loadFailed: 'Try again in a moment.',
    table: {
      name: 'Name',
      tenant: 'Tenant',
      email: 'Email',
      since: 'Member since',
    },
    addButton: 'Add person',
    added: '{{name}} added to {{tenant}}',
    add: {
      title: 'Add a person to {{organisation}}',
      tenant: 'Tenant',
      tenantRequired: 'Choose a tenant',
      person: 'Person',
      personRequired: 'Choose a person',
      signedInOnly: 'Only people who have signed in once can be found.',
      nobody: 'Nobody with an account matches. A person appears after their first sign-in.',
      noTenant: 'This organization runs no tenant yet, so nobody can be added.',
      readFailed: 'The tenants or the people could not be read.',
      save: 'Add',
      failed: 'The person could not be added.',
    },
  },
  programmePeople: {
    table: {
      name: 'Name',
      email: 'Email',
      since: 'Enrolled since',
    },
    addButton: 'Add participant',
    added: '{{name}} enrolled',
    add: {
      title: 'Add a participant to {{programme}}',
      member: 'Member',
      memberRequired: 'Choose a member',
      membersOnly: 'Only members of this organization who are not enrolled can be chosen.',
      nobody: 'No member who is not enrolled yet matches.',
      membersFailed: 'The members could not be read.',
      save: 'Add',
      failed: 'The member could not be enrolled.',
    },
  },
  valueDetail: {
    title: 'Where this value comes from',
    traceTitle: 'Trace of this value',
    loading: 'Reading the origin of this value',
    missing: 'There is no value with this number.',
    failed: 'The origin of this value could not be read.',
    retry: 'Try again',
    value: 'Value',
    time: 'Time',
    notKnown: 'More is not known about this value.',
    recorded: 'As recorded',
    code: 'Code',
    codeSystem: 'Code system: {{system}}',
    source: 'Source',
    sources: {
      manualInput: 'Typed by you',
      deviceSync: 'From a connected source',
      uploaded: 'From a document',
      coachingSession: 'From a coach',
    },
    shownAs: 'Shown as',
    converted: 'How it was converted',
    noConversion: 'No conversion: shown as recorded.',
    conversion: {
      algebra: '1 {{from}} = {{factor}} {{to}}, confirmed on {{date}}.',
      molarMass: '1 {{from}} = {{factor}} {{to}}, through the molar mass of {{analyte}} ({{mass}} g/mol, source: {{source}}), confirmed on {{date}}.',
    },
    reference: 'Reference',
    changedSince: 'Changed since',
    notChanged: 'The code has not changed since.',
    laterRelease: 'A later release changed this code after the value was recorded.',
    change: '{{field}}: {{from}} → {{to}} ({{date}})',
    showFullOrigin: 'Show full origin',
    close: 'Close',
    graphLabel: 'Readings. Use the arrow keys to move between them.',
    graphOpenHint: 'Press Enter to open it.',
  },
  groupingLevels: {
    label: 'How widely values count',
    loading: 'Reading the levels',
    loadFailed: 'The levels could not be read. The level now stored stays as it is.',
    THIS_CODE: {
      name: 'This code only',
      help: 'Only values recorded under this exact code count.',
    },
    ANY_METHOD: {
      name: 'The same thing, whatever the method',
      help: 'Values of the same thing count, whether measured at home or at a clinic.',
    },
  },
};

export default { translation };
